/**
 * AlignedBlocksView.tsx — PLAT-10264 (T5, epic PLAT-10209);
 * Paragraph mode routed through this view in PLAT-10288.
 *
 * The Paragraph / Segment / Fragment rendering for the Translation Review
 * workspace. Lays out the original and translated transcript as **aligned
 * rows**: one row per `AlignedBlock`, original on the left, the block's
 * translated text on the right. The same block drives both columns, so the
 * source unit and its translation always sit on the same row.
 *
 * PLAT-10288 routes Paragraph view through this same view (one aligned row per
 * paragraph group, via `buildParagraphBlocks`). Paragraph blocks flow through
 * the identical render path below, so Paragraph inherits the speaker headers,
 * timestamps, source redline, autoscroll, and click-to-seek the prose-parity
 * follow-up added — there is no Paragraph special-case in the render.
 *
 * Composition, not a fork (epic containment rule D9). This view reuses the
 * exported building blocks rather than the shared wrappers:
 *   - `useTranslatedRender(language, …)` → `currentLead` for the lockstep
 *     active-row highlight (same playhead resolution the translated pane uses)
 *     AND `inScope` — the clip-scope descriptor the prose translated panes use
 *     to drop out-of-clip content; the aligned view scopes its blocks with the
 *     SAME descriptor so the two views never disagree about what's in the clip
 *     (PLAT-10264 clip-scope fix — without it CLIP mode showed pre-clip rows).
 *   - `TranslationStore.getSegments(lang)` → Segment blocks; the active phrases
 *     (`active !== false`) → Fragment blocks; `buildAlignedBlocks` projects both.
 *   - `WordLight` + `transcriptElementToWordClassName` → the original word atom
 *     and its state classes (the original column matches the original pane).
 *   - `Word` (translated atom) → the translated text with the `.current`
 *     playhead style.
 * The shared `TranscriptionText` / `TranscriptionTextTranslated` wrappers are
 * NOT edited or mounted here (they have no subset entry point, and their
 * array-relative `data-index` would mis-highlight a per-block subset).
 *
 * Paragraph-parity behaviors (PLAT-10264 follow-up) — the prose `<Transcript>`
 * renderer had these; this aligned view now matches:
 *   - Inter-word spacing: `renderOriginalWords` walks the contiguous source
 *     range and renders the interstitial whitespace/punctuation elements, like
 *     the prose `TranscriptionTextInner`.
 *   - Click-word-to-seek: every original word and each translated phrase seek
 *     the playhead via the shared `seekToInitialIndex` helper (one
 *     implementation with the prose pane's `seekToIndex`).
 *   - Playback autoscroll: a `[activeBlockId, isPlaying]` effect brings the
 *     active row into view in both columns at `PLAYHEAD_TOP_OFFSET_RATIO`.
 *
 * Speaker headers + timestamps + source redline (PLAT-10264 follow-up — the
 * "Option A reversal"). The first cut of this view was deliberately bare —
 * word-rows only, no speaker headers, removed/cut words dropped (a minimal
 * "Option A — bare word-rows, no speaker headers / prose flow" surface). In-app
 * that read as a flat wall with no provenance, unlike the prose pane. This view
 * now matches the prose pane's reading affordances:
 *   - Per-block speaker header / timestamp: before each block's `<Row>` we emit
 *     a `SpeakerHeader` (speaker name + start time) when the block's speaker
 *     differs from the previous block's, else a `TimestampRow` (start time
 *     only) — the same name/color/time the prose `TranscriptionTextInner`
 *     renders (`getSpeakerNameFromLabel` / `getSpeakerColorFromLabel` /
 *     `formatTime`). The header row and the block `<Row>` are FLAT SIBLINGS in
 *     each `Column` (no wrapper container around a block — wrappers create text-
 *     selection boundaries; CLAUDE.md "Text Selection and DOM Structure"). BOTH
 *     columns emit the identical header sequence (driven by the same blocks /
 *     speakers), so the two columns stay row-aligned. Header rows are
 *     `contentEditable={false}` and non-selectable.
 *   - Source-side redline: in whole-story `renderOriginalWords` does not drop
 *     removed/cut elements — they render struck-through via the `state-removed` /
 *     `state-cut` classes `transcriptElementToWordClassName` already returns,
 *     matching the prose pane's redline. The clip path (`clipScoped`) instead
 *     HIDES the in-clip redline: cut/removed source words are skipped (including
 *     mid-sentence cuts interleaved between kept words) and the orphaned spacer
 *     collapsed. Translated-column redline is OUT OF SCOPE: the
 *     translated column renders one `<Word>` of active translation per block and
 *     carries no per-element removed/cut data to strike (see the translated
 *     column's render note below).
 *
 * DOM scoping (CLAUDE.md #12): the two scroll columns are addressed via local
 * refs only — no `document`-global queries — so a second transcript instance
 * behind the modal is never touched. The autoscroll finds the active row via
 * `[data-active-row]` scoped to each column ref, never `document`.
 */

import { trackTranslationReview_InlineEditCommitted } from '@src/analytics/EventTracker';
import Spinner from '@src/components/common/Spinner';
import { lineFragmentRanges } from '@src/editor/dubbing/DubCoverageService';
import {
  buildReviewDubMap,
  readReviewDubPlayhead,
  reviewFragmentKey,
} from '@src/editor/dubbing/reviewDubPlayhead';
import { fillInWhitespacesInTranscriptElements } from '@src/editor/services/punctuation';
import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import {
  markersFitText,
  placeMarkers,
  serializeLineMarkers,
} from '@src/services/dubbing/lineMarkers';
import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { resolveOverrideSegmentIdForElementIds } from '@src/stores-v2/translationSpanEdit';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import {
  applyLinkedHighlight,
  CHANGED_CARD_ORIGINAL_HIGHLIGHT,
  CHANGED_CARD_TRANSLATED_HIGHLIGHT,
  EMPTY_LINKED_MIRROR,
  LINKED_ROW_TINT,
  resolveLinkedMirror,
} from '@src/utility/linkedCrossLanguageHighlight';
import {
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';
import {
  normalizeForCompare,
  normalizeTypedTranslation,
} from '@src/utility/strings';
import { seekToInitialIndex } from '@src/utility/transcriptEvents';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';

import {
  buildAlignedBlocks,
  buildParagraphBlocks,
  buildRunGroupedBlocks,
  projectPhrasesIntoBlocks,
} from './alignedBlocks';
import {
  buildOverrideOriginalMap,
  CHANGED_PHRASE_TINT,
  changedPhraseHighlightCss,
  findTmTermRanges,
  isDeletionOnlyChange,
  isTmBacked,
  overlayPromoteKey,
  resolveChangedPhrase,
  resolveChangedWordTokens,
  resolveFragmentTmEntry,
  translatedPhraseClassName,
} from './changedPhraseKit';
import { CoverageBadge } from './CoverageBadge';
import {
  type CoverageCache,
  entryForPhrase,
  isCoverageLoadingForPhrase,
  isLastOverlappingPhrase,
} from './coverageCache';
import {
  CoveragePopover,
  INERT_SPLIT_CONTROLS,
  type SplitControls,
} from './CoveragePopover';
import DeliveryMarkerChip from './DeliveryMarkerChip';
import { staleFragmentHighlightCss } from './editableOriginal';
import { stableKeysOf } from './hooks/phraseMembershipMaps';
import { useInlinePhraseEdit } from './hooks/useInlinePhraseEdit';
import { usePhraseEditing } from './hooks/usePhraseEditing';
import { useScrollSync } from './hooks/useScrollSync';
import { useTranslatedRender } from './hooks/useTranslatedRender';
import { InlineEditHint } from './InlineEditHint';
import {
  classifyInlineEdit,
  countWords,
  diffWordsForRedline,
  hasLearnedDoubleClickEdit,
  HINT_MAX_PER_LOAD,
  markDoubleClickEditLearned,
  resolveCaretOffsetAtPoint,
  resolveTranslatedPhraseClickAction,
  summarizeRedline,
} from './inlinePhraseEdit';
import { InlineTranslatedPhraseField } from './InlineTranslatedPhraseField';
import { PhraseEditPencil } from './PhraseEditPencil';
import { wrapUnreadTail } from './readFrontier';
import { StaleFragmentResolveMenu } from './StaleFragmentResolveMenu';
import {
  transcriptElementToWordClassName,
  WordLight,
} from './TranscriptionText';
import { Word } from './TranscriptionTextTranslated';
import { TranslatedPhraseContextMenu } from './TranslatedPhraseContextMenu';
import {
  isTranslationRemoved,
  resolveOriginalValue,
  resolveSourceText,
} from './translatedPhraseEditing';
import { TranslatedPhraseEditorPopover } from './TranslatedPhraseEditorPopover';
import { isPhraseInTranslatedSelection } from './translatedSelectionGuard';
import { buildTranslatedWordAtoms } from './translatedWordAtoms';
import { InlineTextAction } from './translationEditorPrimitives';
import { resolveAppliedTerms } from './translationMemoryCards';
import {
  computePlayheadScrollTop,
  needsSpaceBetweenPhrases,
  PLAYHEAD_TOP_OFFSET_RATIO,
} from './utils';

import type { AlignedBlock, TranslationViewMode } from './alignedBlocks';
import type { TmPromoteSeed } from './changedPhraseKit';
import type { AutoRedubSegmentState } from '@src/components/aiVoices/useAutoRedubOnCommit';
import type {
  DubOnReviewClock,
  ReviewDubPosition,
} from '@src/editor/dubbing/reviewDubPlayhead';
import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';
import type {
  LineMarkers,
  PlacedMarker,
} from '@src/services/dubbing/lineMarkers';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type {
  TranscriptElement,
  TranscriptElementWithClipSegmentAttributes,
} from '@src/types.ts/transcript';
import type {
  CoverageVerdict,
  TranslationPhrase,
} from '@src/types.ts/translation';

/**
 * PLAT-10689 — feature switch for the run-grouped Segment/Fragment layout (split
 * montage-scattered segments into contiguous playback-run rows so the original
 * reads in playback order; see `buildRunGroupedBlocks`). Set to `false` to
 * restore the prior one-block-per-segment layout (`buildAlignedBlocks` +
 * playback-position sort). Only affects the clip path (Content Studio has no
 * scatter). A single toggle so the old behavior is one edit away.
 */
const USE_RUN_GROUPED_BLOCKS = true;

/** The clip-segment attributes `getActiveTranscriptElements` stamps onto each
 *  element (not on the base `TranscriptElement` type — read via this shape). */
interface ClipSegmentAttrs {
  clip_segment_id?: string | number | null;
  clip_segment_position?: number;
  ts?: number | null;
}

export interface ClipScopedElements {
  /** The array the columns render from: a clip-LOCAL, montage-ordered subset in
   *  clip-scoped mode, or `elements` unchanged otherwise. */
  sourceElements: TranscriptElement[];
  /** True only when out-of-clip content was actually dropped. */
  clipScoped: boolean;
  /**
   * Clip-scoped only: `clip_segment_id -> montage rank` (0-based), in
   * `getFullTranscript` timeline order (= montage/playback order), captured
   * BEFORE the display sort. Lets the montage time-map lay out offsets in
   * playback order instead of recording `minTs` order (which mis-maps a segment
   * the montage reorders). Absent on the pass-through (non-clip) returns.
   */
  segmentMontageOrder?: Map<string, number>;
}

/**
 * PLAT-10209 H-A — derive the clip-local element array from the modal's input.
 *
 * In the clip-preview entry the array is the clip-proxied `getFullTranscript`,
 * which carries out-of-clip content as `RemovedTranscriptClip` segments
 * (story-absolute indices, `state: 'removed'`, all members `ts: null`) alongside
 * the real in-clip video segments. We:
 *   - group elements by `clip_segment_id`;
 *   - treat a segment with ≥1 timestamped (`ts != null`) member as IN-clip and a
 *     segment whose members are ALL `ts: null` as OUT-of-clip;
 *   - keep the in-clip segments' word/punct elements (kept AND in-clip
 *     cut/removed — struck source redline), ordered by each segment's min `ts`
 *     (montage / playback order) then `clip_segment_position` within the segment;
 *   - re-insert inter-word whitespace via `fillInWhitespacesInTranscriptElements`
 *     (H-A.1) — the synthetic spacers are added after `getActiveTranscriptElements`
 *     and carry no `clip_segment_id`, so they are dropped by the grouping and
 *     must be rebuilt on the subset, exactly as `getFullTranscript` does.
 *
 * Membership is by SEGMENT, never a bare `ts != null` element filter — that would
 * drop in-clip punctuation (which has no timestamp).
 *
 * `clipScoped` is true only when at least one segment was OUT-of-clip and
 * dropped. With no clip-segment metadata, or when every segment is in-clip
 * (Content Studio whole-story), it returns `elements` unchanged
 * (`clipScoped: false`).
 *
 * Edge (stop condition): a punctuation-only / fully-cut in-clip segment whose
 * members are ALL `ts: null` is indistinguishable from an out-of-clip removed
 * segment here and would be dropped. In-clip text always carries a `ts`, so a
 * normal in-clip segment survives; the "≥1 timestamped member" test would only
 * need refining if such a segment is observed losing wanted struck content.
 */
export function deriveClipScopedElements(
  elements: TranscriptElement[] | undefined,
): ClipScopedElements {
  const els = elements ?? [];
  const segOrder: string[] = [];
  const segments = new Map<string, { positions: number[]; minTs: number }>();
  let sawSegmentMeta = false;
  els.forEach((el, pos) => {
    const a = el as unknown as ClipSegmentAttrs;
    if (a.clip_segment_id === undefined || a.clip_segment_id === null) return;
    sawSegmentMeta = true;
    const id = String(a.clip_segment_id);
    let seg = segments.get(id);
    if (!seg) {
      seg = { positions: [], minTs: Infinity };
      segments.set(id, seg);
      segOrder.push(id);
    }
    seg.positions.push(pos);
    if (typeof a.ts === 'number' && a.ts < seg.minTs) seg.minTs = a.ts;
  });

  // No clip-segment metadata at all ⇒ not a clip preview; pass through.
  if (!sawSegmentMeta) return { sourceElements: els, clipScoped: false };

  const inClipIds = segOrder.filter(
    id => (segments.get(id) as { minTs: number }).minTs !== Infinity,
  );
  // Montage/playback order of segments = first-appearance order in
  // `getFullTranscript` (timeline order) = `inClipIds` BEFORE the display sort
  // below. Capture it as `clip_segment_id -> rank` so the montage time-map can
  // accumulate offsets in playback order; recording `minTs` order mis-maps a
  // segment the montage reorders (PLAT-10209). The display sort below is
  // unchanged.
  const segmentMontageOrder = new Map<string, number>(
    inClipIds.map((id, rank) => [id, rank]),
  );
  // Display order: in-clip segments by min ts, words within a segment by
  // `clip_segment_position`. Drives the rendered columns + phrasesByGroup — NOT
  // the montage order captured above.
  inClipIds.sort(
    (a, b) =>
      (segments.get(a) as { minTs: number }).minTs -
      (segments.get(b) as { minTs: number }).minTs,
  );
  const ordered: TranscriptElement[] = [];
  for (const id of inClipIds) {
    const seg = segments.get(id) as { positions: number[] };
    const segEls = seg.positions
      .map(p => els[p])
      .sort(
        (x, y) =>
          ((x as unknown as ClipSegmentAttrs).clip_segment_position ?? 0) -
          ((y as unknown as ClipSegmentAttrs).clip_segment_position ?? 0),
      );
    ordered.push(...segEls);
  }

  // Clip-scoped iff at least one segment was OUT-of-clip and dropped — a
  // SEGMENT-count test, not an element-count one. (Element-count would misread
  // Content Studio: the whitespace re-fill below changes the count, and the
  // synthetic spacers that lack a `clip_segment_id` would look "dropped".)
  // Content Studio (no segment metadata, or every segment in-clip) ⇒ false ⇒
  // `elements` passed through unchanged.
  const clipScoped = inClipIds.length > 0 && inClipIds.length < segOrder.length;
  if (!clipScoped) return { sourceElements: els, clipScoped: false };

  // Re-insert inter-word whitespace (PLAT-10209 H-A.1). The grouping above kept
  // only word/punct elements (which carry `clip_segment_id`); the synthetic
  // spacers (`initial_index: -1`, added AFTER `getActiveTranscriptElements`)
  // have no segment id and were dropped. `getFullTranscript` builds the working
  // array as flatMap(getActiveTranscriptElements) THEN
  // `fillInWhitespacesInTranscriptElements`, so re-running that fill on the clip
  // subset reproduces the working Original column's spacing exactly.
  const sourceElements = fillInWhitespacesInTranscriptElements(ordered);
  return { sourceElements, clipScoped: true, segmentMontageOrder };
}

/**
 * Format a time in seconds as `MM:SS`. Local copy of the prose renderer's
 * helper (`TranscriptionText.tsx`'s `formatTime`, which is module-private there)
 * so the aligned view's header / timestamp rows show the identical time format
 * the prose pane shows. Mirrors that implementation exactly.
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Per-block speaker + start-time, plus whether this block opens a new speaker
 * section. Derived in block render order (see the `blockHeaders` memo) and
 * consumed by BOTH columns so they emit the identical header/timestamp sequence
 * and stay row-aligned. Exported alongside `renderHeaderRow` for unit testing.
 */
export interface BlockHeaderInfo {
  speakerLabel: string | undefined;
  startTime: number | undefined;
  isNewSpeaker: boolean;
  /**
   * Source `initial_index` of the block's first KEPT word — the caret/seek
   * anchor (see `deriveBlockTimeAnchor`). Carried here so both columns read it
   * off the same per-block map they already read the header off, without a
   * second lookup.
   */
  caretAnchorIndex: number;
}

/**
 * The block's start time AND the source `initial_index` that anchors the
 * caret / seek, both derived from the FIRST KEPT source word — not the lead.
 *
 * Deleted-lead robustness (CLAUDE.md pitfall #9): a block's lead source word
 * can be removed/cut, in which case its runtime `ts` is `null` despite the type
 * saying `number`. Reading the lead's `ts` blindly drops the header/timestamp
 * (null start time) and the paused caret lands on a struck-through word. So we
 * scan the block's source words in index order for the first element that is
 * kept (state not `removed`/`cut`) and carries a numeric `ts`, and use THAT
 * element's `ts` as the start time and its `initial_index` as the caret/seek
 * anchor. Mirrors `useTranslatedRender`'s `buildTranslatedPhraseTimeInfos`,
 * which scans phrase members for a numeric `ts` for the same reason.
 *
 * Falls back to the lead index for the anchor (and `undefined` start time) when
 * no kept word is found — e.g. an all-deleted block — so the caret still has a
 * stable target.
 */
export interface BlockTimeAnchor {
  startTime: number | undefined;
  anchorIndex: number;
}

export function deriveBlockTimeAnchor(
  block: AlignedBlock,
  elementByInitialIndex: Map<number, TranscriptElement>,
): BlockTimeAnchor {
  // `originalIndices` is ascending, so the first kept hit is the earliest kept
  // source word — the same start the prose grouping uses for a section.
  for (const idx of block.originalIndices) {
    const el = elementByInitialIndex.get(idx);
    if (!el) continue;
    if (el.state === 'removed' || el.state === 'cut') continue;
    // `ts` is `number` on text elements but `null`/absent on punctuation and on
    // deleted elements (runtime mismatch). Require a finite number.
    const ts = (el as unknown as { ts: number | null }).ts;
    if (el.type === 'text' && typeof ts === 'number') {
      return { startTime: ts, anchorIndex: idx };
    }
  }
  return { startTime: undefined, anchorIndex: block.lead };
}

/**
 * Per-entry "opens a new speaker section?" flags for the `blockHeaders` memo —
 * extracted as a pure helper so the null-transparency rule is unit-testable
 * without standing up the store / fixtures (PLAT-10264 regression lock).
 *
 * `names` is the RESOLVED display name per block (see the memo): a real name
 * for a block whose anchor word carries a `speaker_label`, or `undefined` for a
 * punctuation / whitespace block whose anchor has no speaker. Returns, per
 * entry, whether that block opens a new speaker section (gates the FULL speaker
 * header vs. a bare timestamp):
 *
 *   - A defined name opens a section ONLY when it differs from the last DEFINED
 *     name seen (so the first real speaker opens one, a true speaker change
 *     opens one, a repeat does not).
 *   - An `undefined` entry is always `false` AND does not update the last-seen
 *     name — null-speaker blocks are transparent: they neither open nor break a
 *     speaker run. This is the bug fix: without it, every null block reset the
 *     run and the next word block re-showed the name on ~every row.
 */
export function markSpeakerSections(
  names: ReadonlyArray<string | undefined>,
): boolean[] {
  let prevSpeakerName: string | undefined = undefined;
  return names.map(name => {
    const isNewSpeaker = name !== undefined && name !== prevSpeakerName;
    if (name !== undefined) prevSpeakerName = name;
    return isNewSpeaker;
  });
}

export interface AlignedBlocksViewProps {
  /** The translated language under review (BCP 47). */
  language: string;
  /** Shared time owner — `videoCreator.time` drives the active-row highlight. */
  videoCreator: ITranscriptVideoCreator;
  /**
   * The transcript element array to render, sourced ONCE by the sole parent
   * (`TranslationReviewWorkspace`) from
   * `useTranscription().getFinalTranscriptionElements()` and passed down
   * un-stamped (PLAT-10464 Atom 1). It is the clip-proxied `getFullTranscript`
   * in the clip-preview entry (out-of-clip content as `RemovedTranscriptClip`
   * segments) or the plain story transcript in Content Studio — the parent sits
   * in each mount's StoreContext, so the correct array arrives on both paths.
   * The parent passes the store's cached stable ref through unchanged, so the
   * `deriveClipScopedElements` memo below stays stable.
   */
  transcriptElements: TranscriptElementWithClipSegmentAttributes[];
  /**
   * `'paragraph'`, `'segment'`, or `'fragment'`. All three render as aligned
   * rows here (PLAT-10288 routes Paragraph through this view too — one aligned
   * row per paragraph group; epic decision Option A). Paragraph blocks flow
   * through the same speaker-header / timestamp / redline / autoscroll /
   * click-seek path Segment/Fragment use, so Paragraph inherits those too.
   */
  viewMode: TranslationViewMode;
  /**
   * Original (left) column element ref — the per-pane container the linked
   * cross-language highlight hook scopes its selection read/paint to
   * (PLAT-10287, extended to this aligned view in PLAT-10264). The workspace
   * passes the SAME ref it attaches to the prose original `<Pane>` in Paragraph
   * mode; only one render path mounts at a time, so the ref resolves to whatever
   * is on screen. Optional — when omitted (e.g. the unit-test mock) the view
   * still scroll-mirrors via its own internal refs. Merged with the internal
   * scroll-sync ref so the single `Column` carries both.
   */
  originalColumnRef?: React.RefObject<HTMLElement>;
  /** Translated (right) column element ref — the highlight hook's translated
   *  per-pane container. Same contract as `originalColumnRef`. */
  translatedColumnRef?: React.RefObject<HTMLElement>;
  /**
   * Renders the translated column ALONE, unmounting the original one — the
   * workspace's expanded-video layout, where the source pane gives its width
   * to the player.
   *
   * The two cross-column mechanisms both stand down cleanly: `useScrollSync`
   * takes this as its `enabled` flag (nothing to mirror to), and the
   * height-match pass already bails on a missing column. Both are keyed on
   * this flag so they RE-BIND when the original column comes back — their
   * refs are stable, so without that the listeners would stay attached to the
   * old detached node and the columns would never re-align.
   */
  hideOriginal?: boolean;
  /**
   * Enables the right-click "Edit translation" menu + popover on each translated
   * phrase, identical to the inline prose view. Default `true` — the review
   * workspace is an editing surface (mirrors `TranscriptionTextTranslated`'s
   * `editable = true` default; there is no read-only mode to thread).
   */
  editable?: boolean;
  /**
   * Clip review modal only: drives the active-word highlight + auto-scroll from
   * the edited-montage renderer instead of `videoCreator`. `montageTime` is the
   * renderer's montage-clock currentTime, converted to the recording clock the
   * transcript timestamps use via a map built from the clip's segment grouping.
   * Omitted on the Content Studio whole-story render — that path keeps reading
   * `videoCreator.time`/`.isPlaying` exactly as before.
   */
  montagePlaybackOverride?: { montageTime: number; isPlaying: boolean };
  /**
   * Clip review modal only: seek the edited-montage renderer to a word. Receives
   * the MONTAGE-clock time (the clicked word's recording `ts` already converted
   * via the in-component map). Provided only on the clip path; when omitted, the
   * word click falls back to `seekToInitialIndex` (the whole-story behavior).
   */
  onMontageSeek?: (montageTime: number) => void;
  /**
   * Clip review modal only (PLAT-10464): true once the clip renderer's editor
   * is ready and reporting montage time. Gates the montage-clock highlight:
   * before it flips true the highlight is suppressed (no active phrase) rather
   * than matching the raw montage clock against a not-yet-valid `currentTime`.
   * The elements' `ts`/`end_ts` already carry the montage clock, so no timing
   * payload is threaded. Omitted/false on the Content Studio whole-story render.
   */
  montageReady?: boolean;
  /**
   * PLAT-11906 — the dub the review is playing, on the same clock as the
   * highlight (the heard dub's lines and words as the captions place them).
   * Set whenever the voice is Dubbed or Both, paused or not: the read shading,
   * the playing segment and the follow-scroll then come from the dub's own
   * times instead of the source words', so a pause never drops back to English
   * timing. `null` / omitted (Original) keeps the source timing.
   */
  dubPlayhead?: DubOnReviewClock | null;
  /**
   * PLAT-10268 (T8) — the workspace's per-lead coverage verdict cache, keyed by
   * the fragment's LEAD stable id (leadUuid). When present, a color-banded
   * `CoverageBadge` is shown below the translated block's timestamp when its
   * STABLE IDS overlap a FRESH cached span (`verdictForPhrase` — id overlap, not
   * exact lead, so it survives a re-fix re-split, BUG B).
   * Additive: omitted (or no verdict for a fragment) renders nothing. A stale
   * fragment carrying no ids never overlaps and so never shows a badge.
   * Review-scoped — only the Translation Review workspace passes it.
   */
  coverageByLead?: CoverageCache;
  /**
   * PLAT-11678 — the automatic re-dub's state per translation segment id
   * (`TranslationStore.getSegments`: a phrase's `parentId`, else its lead
   * element id). A block whose phrases belong to a segment in this map shows
   * one status line under its translated text: `Updating dubbing…`, a brief
   * `Dub updated`, or `Dub out of date` with a Retry. Absent ⇒ nothing.
   */
  dubStateBySegment?: ReadonlyMap<string, AutoRedubSegmentState>;
  /**
   * PLAT-12034 — each dub line's delivery markers (by `p_` line id), drawn as
   * chips inside the line's words on the Dubbing tab. Absent ⇒ no chips.
   */
  dubMarkers?: ReadonlyMap<string, LineMarkers>;
  /**
   * A chip's Remove: re-dub the line under its remaining markers, given as
   * the marked-up text, or `null` when none remain.
   */
  onMarkerRemove?: (lineId: string, taggedText: string | null) => void;
  /** A chip's Edit Markers…: open the line's markers editor at the chip. */
  onMarkerEdit?: (lineId: string, anchor: HTMLElement) => void;
  /** Retry the failed re-dub of this translation segment. */
  onRetryDub?: (segmentId: string) => void;
  /**
   * PLAT-10268 (T8) QA polish — the set of fragment lead ids whose coverage check
   * is currently in flight (SPEC AC 4). While a lead id is in this set, its
   * coverage slot shows a loading spinner instead of the badge; the spinner is
   * replaced by the `CoverageBadge` when the verdict resolves (and the lead id
   * leaves the set). Review-scoped — only the Translation Review workspace passes it.
   */
  loadingCoverageLeads?: ReadonlySet<string>;
  /**
   * PLAT-10268 (T8) — Atom 6. The fragment lead id whose coverage details popover
   * is open (held in the workspace, AC 1), or `null`. When it matches a rendered
   * fragment's leadUuid, the `CoveragePopover` mounts anchored to that fragment's
   * badge. Review-scoped — only the Translation Review workspace passes it.
   */
  openCoverageLead?: string | null;
  /** Toggles the coverage popover open/closed for a fragment lead id. */
  onCoverageBadgeClick?: (lead: string) => void;
  /** Closes the coverage popover (close control, Escape, click-outside). */
  onCoverageClose?: () => void;
  /** Undo from the popover: revert the fragment + clear its cached verdict. */
  onCoverageUndo?: (lead: string) => void;
  /**
   * PLAT-10257 (A4) — the popover's "Fix Translation": open the shared
   * two-mode span editor for the anchored segment's covered words. PURE
   * pass-through to `CoveragePopover` — the workspace resolves the covered
   * span and the overlap guard; nothing is computed here. Keyed by the same
   * fragment LEAD ELEMENT ID as `onCoverageUndo` (stable-id migration).
   * Optional and review-scoped; when omitted the popover renders no such button.
   */
  onCoverageFixTranslation?: (lead: string) => void;
  /**
   * PLAT-10268 — fired after the phrase-editor popover SAVES an edit (the
   * re-fix path: click on green edited text, or right-click → Edit
   * translation), with the edited phrase's source word stable ids
   * (`originalElementIds`). The
   * review workspace wires this to the same coverage-check trigger the
   * span-replace confirm uses (`handleSpanConfirmed`), so re-fixing an
   * already-overridden segment shows the spinner + updated badge too.
   * Optional and review-scoped — other surfaces rendering this view (or the
   * popover via the prose views) are untouched when omitted.
   */
  onPhraseEditSaved?: (coveredElementIds: string[]) => void;
  /**
   * PLAT-11335 — resolves the fresh coverage verdict covering the given source
   * word stable ids, or `undefined` when none exists. Unlike `coverageByLead`
   * (which the workspace withholds outside Segment view to keep the transcript
   * COLUMN badges segment-only, QA round 2), this is passed in ALL views: it
   * feeds only the phrase-editor popover's badge, so a Paragraph/Words reviewer
   * editing a checked phrase still sees its score. Review-scoped.
   */
  resolveCoverageForPhrase?: (
    originalElementIds: readonly string[],
  ) => CoverageVerdict | undefined;
  /**
   * PLAT-10268 (T8) — Atom 7. The split-control provider passed through to the
   * `CoveragePopover` (Split / Undo split / Redo split). The workspace supplies a
   * placeholder until PLAT-10270 ships the real collapse / reapply operations.
   * When omitted, the popover renders the split buttons disabled (safe no-ops).
   */
  splitControls?: SplitControls;
  /**
   * One-shot "bring this source word into view in BOTH columns" request, fired
   * by the workspace when a Changes-panel card is clicked (the playback
   * autoscroll is playing-only, so a paused seek alone can leave the change
   * offscreen). The nonce re-fires on repeat clicks of the same card — the
   * `pendingMontageSeek` pattern. Optional / opt-in (D9).
   */
  revealRequest?: { initialIndex: number; nonce: number } | null;
  /**
   * Hovering a CHANGED (edited-green) translated phrase reports the change's
   * Changes-panel entry key — the override segment id (`o_…`) or
   * `f:<originalIndices>` for a single-phrase edit, matching
   * `TranslationChangesPanel`'s `ChangeEntry.key` — and `null` on leave, so the
   * workspace can highlight the matching card (the reverse of the card-hover
   * alignment reveal). Optional / opt-in (D9).
   */
  onChangedPhraseHover?: (changeKey: string | null) => void;
  /**
   * Source indices of the Changes-panel card under the pointer (null when
   * none). This view owns the block-aware emphasis: a block whose indices are
   * FULLY covered tints its whole row (calm, one wash per column) instead of
   * painting every word; only partially-covered blocks get the word-level blue
   * correspondence highlight. Optional / opt-in (D9).
   */
  hoveredChangeIndices?: readonly number[] | null;
  /**
   * Proxy REFERENCE language for the left column (PLAT-10655): when set, the
   * left column renders THAT language's phrases (read-only — no redline, no
   * editing) aligned to the same reviewed-language blocks, instead of the
   * original transcript. For reviewers who don't speak the source language.
   * Null/absent ⇒ the original renders as today. Optional / opt-in (D9).
   * Known degrade while active: the span-replace lock and card-hover word
   * paint resolve at proxy-phrase-lead granularity (the pane's
   * `data-initial-index` anchors are phrase leads, not words); row-level
   * emphasis is unaffected.
   */
  referenceLanguage?: string | null;
  /**
   * PLAT-10266 — the set of STALE translated-fragment leads
   * (`min(originalIndices)`), computed by the review's in-session staleness
   * selector after the reviewer edits the ORIGINAL column. When a rendered
   * fragment's `lead` is in this set it paints AMBER (`stale`), distinct from
   * green(edited)/gold(playhead)/blue(linked). Optional + review-scoped — only
   * the whole-story Translation Review passes it; omitted ⇒ nothing paints amber.
   */
  staleLeads?: ReadonlySet<number>;
  /**
   * PLAT-10266 (Card 2) — resolve a STALE fragment via "Re-translate (machine)".
   * When wired (whole-story review only), right-clicking an amber fragment opens
   * the inline resolve menu instead of the plain "Fix Translation" pill; the menu
   * calls this with the fragment's `lead`. Optional + review-scoped — omitted on
   * the clip host and every shared prose home, so the resolve affordance never
   * appears there (AC6). Card 3 adds a "Replace manually" sibling to the menu.
   */
  onResolveRetranslate?: (lead: number) => void;
  /**
   * PLAT-10266 (Card 3) — resolve a STALE fragment via "Replace manually": the
   * inline resolve menu's second row, opening the shipped type-replacement span
   * editor on the fragment (DESIGN §7). Wired under the SAME whole-story gate as
   * `onResolveRetranslate`; omitted on the clip host and shared prose homes (AC6).
   * The menu renders the manual row only when this is present.
   */
  onResolveManual?: (lead: number) => void;
  /**
   * PLAT-10266 (Card R2) — the lead of the stale fragment the CURRENT translated-column
   * selection covers, or null. Computed once by `useSpanReplace` (which owns the
   * selection) and handed down so both triggers route identically.
   *
   * It exists for the right-click half of the defect: the selection-precedence bail runs
   * BEFORE the stale check, so right-clicking an amber word while a selection covered it
   * yielded "Fix Translation". Right-click now consults this first — but ONLY when it
   * matches the clicked fragment's own canonical key, so a MIXED selection still bails to
   * the span path (PLAT-10467 stands, and Denys ruled mixed keeps "Fix Translation").
   */
  staleSelectionLead?: number | null;
  /**
   * PLAT-10266 (Card R2) — imperative opener for the inline resolve menu, whose state is
   * local to this component. The review workspace calls it when a HIGHLIGHT resolves to a
   * single stale fragment, passing the fragment's lead and the anchor element the hook
   * already resolved. Mirrors the existing `openEditorRef` idiom in
   * `InlineSpanReplaceLayer` rather than inventing a second pattern; reassigned each
   * render and cleared on unmount.
   */
  resolveMenuOpenerRef?: React.MutableRefObject<
    ((hit: { lead: number; anchorElement: HTMLElement } | null) => void) | null
  >;
  /**
   * PLAT-10850 (E13) — the workspace's glossary entries keyed by id, used to
   * underline ONLY the matched dictionary term inside a TM-backed translated
   * fragment (not the whole fragment). Best-effort: absent / empty ⇒ no term
   * underline (the fragment still renders normally). Loaded once by the
   * workspace and shared with `TranslationMemoryPanel`.
   */
  tmEntriesById?: ReadonlyMap<string, TranslationMemoryEntry>;
  /**
   * PLAT-10852 (E15) — collection id → name, so the edit popover's TM
   * membership section can say which collection an applied entry came from.
   * Built once by the workspace (same map the TM panel uses); absent ⇒ the
   * section labels every entry "Default".
   */
  tmCollectionNameById?: ReadonlyMap<string, string>;
  /**
   * PLAT-10852 (E15) — "Save to Translation Memory" on the edit popover. The
   * host opens the entry editor seeded with this fragment's source text and the
   * reviewer's current translation. Absent (flag off / no workspace) ⇒ the
   * action doesn't render.
   */
  onPromoteToTm?: (seed: TmPromoteSeed) => void;
  /**
   * PLAT-10852 — entry ids promoted in this session, keyed by `fragmentKeyOf`.
   * A just-promoted fragment has no backend `tmEntryIds` until a re-translate,
   * so this is what lets the overlay show "Translation Memory Entry" straight
   * after the save instead of offering to create a duplicate.
   */
  promotedTmEntryIds?: ReadonlyMap<string, string>;
  /**
   * PLAT-10852 — open the entry behind a fragment. The host owns the modal.
   * Absent ⇒ no link (the promote action shows instead, where applicable).
   */
  onViewTmEntry?: (entry: TranslationMemoryEntry) => void;
}

/**
 * Render one block's original-language words using the shared `WordLight` atom
 * + state classes (the original column matches the original prose pane).
 *
 * Spacing (PLAT-10264 follow-up): the transcript stores inter-word whitespace
 * and punctuation as *separate* elements. `block.originalIndices` lists only the
 * sparse content-word indices, and for a multi-phrase Segment those content
 * indices are NOT contiguous — the interstitial whitespace between phrases sits
 * at indices that are in neither phrase's set. The earlier `belongs`-Set guard
 * dropped any non-whitespace element outside `originalIndices`, but it also let
 * the *contiguous-range* assumption break multi-phrase blocks (gaps in the set
 * meant the run of words between two phrases rendered with no spaces:
 * "littleknownsecret"). So we now walk the block's ARRAY-POSITION span — from
 * the lowest to the highest position its source words occupy in
 * `sourceElements`, NOT the positions of its smallest / largest `initial_index`.
 * On a montage-reordered clip `sourceElements` is in playback/recording order, so
 * a moved phrase's smallest `initial_index` can sit LATE in the array; keying the
 * span off `originalIndices[0]`/`[last]` then started the walk at that late
 * position and dropped every earlier-in-array phrase of the block (the Paragraph
 * left-column "missing reordered phrase" bug). Scanning for the min / max
 * position covers the block's full run regardless of index order. We render EVERY
 * element in the span, in document order, exactly like the prose renderer
 * `TranscriptionTextInner` (TranscriptionText.tsx): skip ONLY leading whitespace
 * (the `seenVisible` guard), render everything else — including removed/cut
 * elements, which paint struck-through (the source-side redline). This restores
 * the source's native 1:1 spacing for both single- and multi-phrase blocks.
 *
 * Foreign-word interleave (PLAT-10209 — the segment/fragment "wrong/duplicated
 * original" fix): the span holds only the block's own elements + separators for
 * a Paragraph block (Paragraph groups tile `sourceElements` in contiguous array
 * runs) and for Segment/Fragment on a non-reordered clip / whole-story (array
 * order == index order). But on a montage-REORDERED clip a Segment's fragments —
 * or even a single phrase carrying a MOVED word — are scattered across playback
 * order, so its `[firstPos..lastPos]` span INTERLEAVES other blocks' words.
 * Rendering the whole span then duplicated other segments' text into this row
 * and repeated the same text across rows (the reported bug). On the clip path
 * (`clipScoped`) those foreign content words are skipped below, so only the
 * block's own words render (in playback/array order — matching the reordered-
 * phrase behavior the min/max-position slice already gives). Whole-story is
 * untouched: no foreign words fall inside a contiguous block's span.
 *
 * Active highlight (PLAT-10264, restyled by PLAT-11765) — unlike the prose pane
 * (`adjustDOMClassesToCursor` in TranscriptionText.tsx), the review draws NO
 * play-state distinction, no solid fill and no caret. Every word of the active
 * block gets `playing`, which reads slightly bolder without changing the text's
 * metrics, so the marker never lands on the wrong word and never moves while a
 * segment plays. Word-by-word progress is carried by `read` instead (see
 * `readUntilTime`); `PLAYHEAD_USES_FILL_OR_CARET` above says why the gold fill
 * and then the caret both went. The translated column carries the same `playing`
 * marker on its `Word`.
 *
 * Redline (PLAT-10264 follow-up — the Option-A reversal): removed / cut elements
 * are NO LONGER dropped — they render struck-through via the `state-removed` /
 * `state-cut` classes `transcriptElementToWordClassName` returns (which
 * `WordLight` paints as a line-through, matching the prose pane's redline). Only
 * LEADING whitespace is still dropped so the row doesn't open with an indent.
 *
 * Click-to-seek (PLAT-10264 follow-up): each word carries `data-index` (its
 * source `initial_index`) and an `onClick` that seeks the playhead to that
 * element's `ts` via the shared `onWordClick` callback — parity with the prose
 * pane's click-to-seek. Whitespace tokens default to `cursor: default` in
 * `WordLight`, so clicking the gap is a harmless no-op (its `ts` is undefined).
 */
// Exported for unit testing the spacing/whitespace logic without mounting the
// store-connected component (the surrounding view needs transcription +
// translation store context). Not part of the view's public API.
/**
 * PLAT-11765 — how the Translation Review marks the playhead. NOT with the gold
 * fill the prose panes use (`.highlighted` / `.current`), and not with a caret
 * either: both assert a precision this screen's timings do not have (they ride
 * on original-language word times the audio does not honor — under a dub the
 * lines are re-placed and the offset accumulates), and a marker that jumps and
 * reflows the text as it moves is more distracting than useful. Instead the
 * playing segment is set slightly BOLDER (`playing`), which stays put, and the
 * read/unread frontier carries the fine-grained progress. Passed where the
 * shared class helpers take `isCurrent` / `isPlaying`, both of which only ever
 * produce the fill or the caret.
 */
const PLAYHEAD_USES_FILL_OR_CARET = false;

export function renderOriginalWords(
  block: AlignedBlock,
  originalElements: readonly TranscriptElement[],
  posByInitialIndex: Map<number, number>,
  isActive: boolean,
  isPlaying: boolean,
  onWordClick: (initialIndex: number) => void,
  // Clip path only (PLAT-10209): hide the in-clip redline. When true, cut/removed
  // source words in the slice are skipped (not rendered struck) and the spacer
  // orphaned next to a skipped word is collapsed so two kept words stay single-
  // spaced. Defaults false so Content Studio whole-story renders struck as today.
  clipScoped: boolean = false,
  // PLAT-10611 — stable element UUID (`source_uuid ?? id`) -> array position:
  // the drift-correct twin of `posByInitialIndex`. When present (and the block
  // carries `originalElementIds`) the slice bounds AND the foreign-word
  // membership test resolve by UUID FIRST — correct even on a reused/reordered
  // clip whose `initial_index` has shifted from story space. Optional: callers
  // that omit it (older unit tests) fall back to the integer path unchanged.
  posBySourceUuid?: Map<string, number>,
  // PLAT-11765 — the playhead time in the same clock as the elements' `ts`
  // (recording on whole-story, packed montage on the clip path). Every word
  // whose `end_ts` has passed gets the `read` class, so the transcript shows
  // how far playback has got the way WhatsApp / Spotify transcripts do.
  // Undefined (no clock yet, or a non-review caller) leaves every word unread.
  readUntilTime?: number,
  // PLAT-11906 — while the review plays a dub: whether a word's dub line has
  // started, which then decides its read state instead of its English time.
  // `undefined` for a word no dub line voices, which keeps the English rule.
  isReadByDub?: (el: TranscriptElement) => boolean | undefined,
): React.ReactNode {
  if (block.originalIndices.length === 0) return null;

  // Render the source ARRAY SLICE covering the block, in array order — NOT a
  // walk of `initial_index` values. The transcript interleaves inter-word
  // whitespace as SYNTHETIC elements with `initial_index === -1` (utils.ts:
  // synthetics around boundary whitespace). Those are unreachable by an
  // initial_index walk and excluded from any initial_index-keyed map — which is
  // exactly why a previous "walk the index range" attempt still produced
  // "littleknownsecret". The prose renderer (`TranscriptionTextInner`) walks the
  // real element array and emits them, so its spaces appear. We must do the
  // same: slice between the block's first and last content word's ARRAY POSITION
  // and render every element in between, including the synthetic spacers.
  // Bound the slice by the block's MIN and MAX array position (see the header
  // note) — not the positions of its smallest / largest `initial_index`, which
  // diverge from array order on a montage-reordered clip and dropped the block's
  // earlier-in-array phrases.
  // The block's OWN elements as a stable-UUID set (PLAT-10611) — the drift-
  // correct membership key, used for both the slice bounds and the foreign-word
  // skip. Present whenever the block carries `originalElementIds`; `undefined`
  // on a legacy blob, where the integer `originalIndices` set below stands in.
  const ownElementIds =
    block.originalElementIds && block.originalElementIds.length > 0
      ? new Set(block.originalElementIds)
      : undefined;
  // Integer twin, kept ONLY as the per-element fallback for data that carries no
  // stable id. The UUID path is preferred everywhere ids exist.
  const ownIndices = new Set(block.originalIndices);

  // Slice bounds = the block's MIN and MAX ARRAY POSITION (see the header note).
  // Resolve each own element's position by its stable UUID FIRST (drift-correct
  // on a reused/reordered clip whose `initial_index` shifted from story space);
  // fall back to the integer `initial_index` positions when the block has no
  // element ids or no UUID map was passed.
  let firstPos = Infinity;
  let lastPos = -Infinity;
  if (ownElementIds && posBySourceUuid) {
    for (const id of ownElementIds) {
      const pos = posBySourceUuid.get(id);
      if (pos === undefined) continue;
      if (pos < firstPos) firstPos = pos;
      if (pos > lastPos) lastPos = pos;
    }
  }
  if (firstPos === Infinity) {
    for (const idx of block.originalIndices) {
      const pos = posByInitialIndex.get(idx);
      if (pos === undefined) continue;
      if (pos < firstPos) firstPos = pos;
      if (pos > lastPos) lastPos = pos;
    }
  }
  if (firstPos === Infinity) return null;

  // PLAT-10266 (2026-07-28) — a word REPLACED at this block's EDGE would otherwise
  // fall outside the window. "Replace Term" DELETES the original token and splices
  // an `initial_index:-1` / `state:'added'` token that carries no `source_uuid`, so
  // the replacement anchors nothing: it is in neither map, contributes no position,
  // and when the replaced word was the block's FIRST covered word the window opens
  // at the next SURVIVING word instead. The row then rendered ", Samsung came out
  // with …" — the two replaced words gone, the stranded comma its fingerprint
  // (Denys 2026-07-28; proven live in logs/bug-popover-routing-20260728/).
  //
  // So walk outward across the contiguous run of INSERTED tokens (the Card 6 idiom,
  // which already extends the staleness signature window forward the same way).
  //
  // PLAT-10266 Card I4 (2026-08-05) — WHEN an end claims that run. Since Cards I1/I2
  // a replacement word INHERITS the stable key of the word it replaced
  // (`inheritedSourceUuidsFor`), positionally: the i-th new word takes the i-th
  // replaced word's key, so surplus new words inherit nothing. "The covered key
  // failed to resolve" was therefore only ever a PROXY for "a replacement sits
  // here", and it worked only while editing DESTROYED the key. It now survives onto
  // the FIRST replacement word, and the un-inherited words after it rendered in no
  // row at all (replacing `secret` with `hidden truth` showed "little hidden").
  // Ownership is now established POSITIVELY at the trailing end: a covered key that
  // resolves ONTO an inserted token means the replacement run is this block's.
  //
  // The two ends are deliberately NOT symmetric. Because the FIRST replacement word
  // takes the FIRST replaced word's key, a block's leading edge already opens on its
  // own run and only the run's TAIL can be unanchored. Giving the leading edge the
  // same positive trigger would make a block reach BACKWARD into its left-hand
  // neighbour's run and render the neighbour's words twice — the very failure the
  // original rule existed to prevent. So the leading edge keeps the
  // failed-to-resolve trigger alone (the legacy shape: data edited before Cards
  // I1/I2, or a duplicated key the inheritance guard refused to hand on).
  //
  // Both walks stop dead at a FOREIGN ANCHOR — an inserted content word carrying a
  // stable key this block does not cover, which since Cards I1/I2 is exactly where
  // the next block's replacement run begins. That is what keeps an innocent
  // neighbour's inserts out of this row.
  //
  // The insert test is STRUCTURAL, not the `state:'added'` marker: a save can clear
  // that marker (measured live — a later edit re-saved the story and flattened the
  // earlier edit's `added` states to undefined, leaving the replacement tokens
  // marker-less but still unanchored), whereas `initial_index < 0` on a token that
  // came from no original transcript position cannot be erased. The walk crosses the
  // inserted words AND the spacers between them, and stops dead at the first element
  // carrying a real story index — i.e. at the neighbouring ORIGINAL word. A
  // delete/cut is unaffected either way: it leaves a struck token that keeps its id,
  // so it still resolves and already sits inside the window where it belongs.
  const isInsert = (pos: number): boolean =>
    (originalElements[pos]?.initial_index ?? 0) < 0;
  // Spacing-only element. Inter-word transcript whitespace ALSO carries
  // `initial_index < 0`, so `isInsert` alone cannot tell a spacer minted inside a
  // replacement run from the ordinary spacer that follows the run.
  const isBlankAt = (pos: number): boolean => {
    const value = originalElements[pos]?.value;
    return typeof value === 'string' && value.trim() === '';
  };
  // A FOREIGN ANCHOR: an inserted content word carrying a stable key this block
  // does not cover — i.e. the first word of the NEXT block's replacement run.
  const isForeignAnchor = (pos: number): boolean => {
    const key = originalElements[pos]?.source_uuid;
    if (key === undefined || !ownElementIds) return false;
    if (isBlankAt(pos)) return false;
    return !ownElementIds.has(key);
  };
  const orderedOwnIds = block.originalElementIds;
  if (block.arraySpan) {
    // PARAGRAPH blocks carry no `originalElementIds`, so there is no vanished key to
    // detect — but the group's own span IS the authority on what belongs to this
    // paragraph. Reach the inserts inside it and stop at its bounds, which is what
    // keeps a neighbouring paragraph's inserts out.
    const { from, to } = block.arraySpan;
    while (firstPos > from && isInsert(firstPos - 1)) firstPos -= 1;
    while (lastPos < to && isInsert(lastPos + 1)) lastPos += 1;
  } else if (orderedOwnIds && orderedOwnIds.length > 0 && posBySourceUuid) {
    // Segment / Fragment blocks (see the note above for why the two ends differ).
    // LEADING edge: only the legacy shape, where the block's own first covered key
    // was destroyed by the edit and the run therefore sits entirely to its left.
    if (posBySourceUuid.get(orderedOwnIds[0]) === undefined) {
      while (
        firstPos > 0 &&
        isInsert(firstPos - 1) &&
        !isForeignAnchor(firstPos - 1)
      ) {
        firstPos -= 1;
      }
    }
    // TRAILING edge: the key was destroyed (legacy) OR it resolves ONTO an inserted
    // token, which is the positive proof that this block owns the run sitting there.
    const lastOwnPos = posBySourceUuid.get(
      orderedOwnIds[orderedOwnIds.length - 1],
    );
    if (lastOwnPos === undefined || isInsert(lastOwnPos)) {
      // End on the last inserted NON-BLANK token, never on a spacer: the run's
      // trailing whitespace belongs between the rows, not to this one. Same shape as
      // `resolveVanishedSpanSource`'s `runEnd`, which only advances on real tokens.
      //
      // PLAT-10266 Card I6 — WHO OWNS A COMMA OR FULL STOP AT THE BOUNDARY between
      // two rows' replacement runs. Only TEXT tokens inherit a key (the branch below
      // in `splitTokenValueIntoTokens`), so a mark sitting between two runs is not a
      // foreign anchor and nothing in the data says which run it came from. It is
      // provably undecidable, not merely hard: an array built by editing one row's
      // word to `x y,` and the next row's to `p q` is byte-identical to one built by
      // editing them to `x y` and `, p q` — same values, types, `initial_index`,
      // `source_uuid` AND minted `id`s (measured, Card I6). So this is a pure choice
      // of side, and the side chosen here is THE LEFT ROW: the mark is rendered and
      // re-translated by the row whose run it directly follows, which is why the
      // walk below ends on the last inserted non-blank token rather than backing off
      // to the last inserted WORD (what Card I5 did, and which gave the mark to
      // NEITHER row — the following row's own first key resolves onto its own first
      // word, so its leading-edge walk never fires and its slice starts after the
      // mark).
      //
      // THE LEFT ROW IS NOT ALWAYS THE ROW THAT TYPED THE MARK. When the mark opens
      // the FOLLOWING row's replacement (`, p q`) the left row shows one mark too
      // many. That is accepted deliberately, for two reasons. First, being wrong on
      // this side reads as ordinary prose (`x y,`) whereas being wrong on the other
      // side makes a row open with a bare mark (`. c1 c2`), which reads as a
      // rendering bug — the following-row rule was implemented and measured before
      // this one was chosen. Second, the disputed mark is always one a reviewer typed
      // INSIDE a replacement — `Transcript`'s constructor stamps `initial_index: idx`
      // on EVERY loaded element including punctuation (`Transcript.ts:41`), so an
      // original mark is not an insert and neither walk can cross one — and
      // replacement text that ENDS
      // with a mark is the ordinary case while replacement text that BEGINS with one
      // is unusual — so the left row is the typing row most of the time. That is an
      // argument from how the edit is made, not a measured frequency.
      //
      // NEVER TWO, unconditionally: the following row cannot also claim the mark,
      // because its leading-edge walk fires only in the legacy shape where its own
      // first covered key was destroyed — and in that shape the two rows already
      // overlap on WORDS (the known-unfixable FN2 case, which only exists for
      // stories edited before this work and heals on re-edit).
      //
      // NEVER ZERO only when the LEFT element is itself part of an edited run.
      // Qualified after review 4 (F1), which proved the unconditional promise this
      // comment used to make was false. If a replacement BEGINS with a mark and the
      // element to its left is a surviving ORIGINAL word, NEITHER walk fires: the
      // left row's last covered key resolves onto that original word, not onto an
      // insert, so the trailing walk above never starts; and the right row's own
      // first covered key resolves onto its own first word — the mark carries no
      // key, only TEXT tokens inherit one — so its leading walk never starts either.
      // That mark is rendered by no row. It PREDATES this whole card series (neither
      // trigger was introduced here) and is out of this card's scope; recorded so the
      // next reader is not misled into believing exactly-once is unconditional.
      let runEnd = lastPos;
      let scan = lastPos + 1;
      while (scan < originalElements.length && isInsert(scan)) {
        if (isForeignAnchor(scan)) break;
        if (!isBlankAt(scan)) runEnd = scan;
        scan += 1;
      }
      lastPos = runEnd;
    }
  }

  const nodes: React.ReactNode[] = [];
  let seenVisible = false;
  // PLAT-11765 — carried across the loop so punctuation and whitespace, which
  // have no timing of their own, inherit the read state of the word before
  // them; otherwise a read sentence would keep gray commas between white words.
  let lastWordRead = false;
  // Clip path: a struck word skipped below orphans its adjacent spacer; drop the
  // following whitespace so two kept words don't end up double-spaced.
  let dropNextWhitespace = false;
  for (let pos = firstPos; pos <= lastPos; pos++) {
    const el = originalElements[pos];
    if (!el) continue;
    // Redline (PLAT-10264 — Option-A reversal): whole-story does NOT drop
    // removed/cut elements — they render struck-through via the `state-removed` /
    // `state-cut` classes `transcriptElementToWordClassName` returns, matching
    // the prose pane's source-side redline. The clip path (`clipScoped`) instead
    // HIDES the in-clip redline: cut/removed are skipped just below.
    const isWhitespace = typeof el.value === 'string' && el.value.trim() === '';

    // Drop leading whitespace so the row doesn't open with an indent.
    if (!seenVisible && isWhitespace) continue;

    // Clip path redline gate: never show in-clip struck (cut/removed) words —
    // including mid-sentence cuts interleaved between kept words. Skip the
    // element and mark its trailing spacer for collapse (mirrors the leading-
    // whitespace guard above). Whole-story (`clipScoped` false) is untouched.
    if (clipScoped && (el.state === 'removed' || el.state === 'cut')) {
      dropNextWhitespace = true;
      continue;
    }
    // Clip path (reordered-montage fix, PLAT-10611): skip a CONTENT word that
    // belongs to ANOTHER block. A scattered segment/fragment's
    // `[firstPos..lastPos]` span covers the words the montage interleaved
    // between its own runs; rendering them duplicated other segments' text into
    // this row (and repeated the same text across rows). Membership is by stable
    // element UUID FIRST (`source_uuid ?? id` vs the block's
    // `originalElementIds`) — drift-correct where a reused/reordered clip has
    // shifted `initial_index` from story space — falling back to the integer
    // `initial_index` per element only when an id is missing (legacy). Drop the
    // foreign word and collapse its orphaned spacer (same mechanism as the
    // redline gate above). Whole-story is untouched (`clipScoped` false →
    // contiguous block, no interleave).
    //
    // SCOPE NOTE (PLAT-10266 Cards I1–I5, 2026-08-05): the widened slice above —
    // the walk that claims a block's replacement run — is WHOLE-STORY only. It
    // does not change what this clip-scoped column shows. A SURPLUS replacement
    // word carries no `source_uuid` and a minted `id`, so it is not in the
    // block's `originalElementIds` and this gate drops it however far the slice
    // reaches: the clip-scoped column still shows "little hidden" where the
    // whole-story column now shows "little hidden truth". That is a bounded gap,
    // not a regression — before these cards the column showed no replacement
    // text at all, and the inherited key on the FIRST replacement word improves
    // it by one word. Deliberately out of this ticket's scope.
    if (clipScoped && !isWhitespace) {
      const elUuid = el.source_uuid ?? el.id;
      const isForeign =
        ownElementIds && elUuid
          ? !ownElementIds.has(elUuid)
          : el.initial_index >= 0
            ? !ownIndices.has(el.initial_index)
            : false;
      if (isForeign) {
        dropNextWhitespace = true;
        continue;
      }
    }
    if (dropNextWhitespace && isWhitespace) {
      dropNextWhitespace = false;
      continue;
    }
    dropNextWhitespace = false;

    seenVisible = true;

    // No photo-highlight context in review → omit the resolver; the helper
    // falls back to the no-photo sentinel (same branch as hidePhotoHighlights).
    const stateClassName = transcriptElementToWordClassName(
      el,
      el.initial_index,
      undefined,
      true,
    );
    let className = stateClassName;
    // PLAT-11765 — how far playback has got, WORD BY WORD. A word brightens the
    // moment the playhead reaches its START (`ts`), not when it finishes: you
    // read a word while you hear it, so waiting for `end_ts` left the word being
    // spoken looking unread, and brightening the whole playing segment at once
    // was coarser still. Timing-less
    // elements (punctuation, spacers, struck words whose `ts` is null) inherit
    // the last real word's state. Both classes are declared FIRST in
    // `WordLight`, so every redline / diff state color still wins over them on
    // equal specificity.
    if (readUntilTime !== undefined) {
      const byDub = isReadByDub?.(el);
      if (byDub !== undefined) {
        lastWordRead = byDub;
      } else if (typeof el.ts === 'number') {
        lastWordRead = readUntilTime >= el.ts;
      }
      className = `${className} ${lastWordRead ? 'read' : 'unread'}`.trim();
    }
    // PLAT-11765 — the playing segment reads slightly BOLDER. That is the whole
    // playhead marker here: no gold fill and no caret. Both of those point at
    // one word, which this screen cannot honestly do (the timings are the
    // original language's, and under a dub the lines are re-placed, so the
    // marker drifts), and both move — a caret pinned to the active fragment
    // also fell behind its own column's read frontier, since `read` advances
    // word by word inside that fragment. Weight applies to the whole segment at
    // once, so it never lands on the wrong word and never moves while a segment
    // plays. `isPlaying` is no longer read here; it stays in the signature for
    // the callers and tests that pass it positionally. The caret anchor and the
    // active-phrase range this used to take are gone with the marker they fed.
    // Struck words are not in the audio, so they are not "playing" — marking
    // them would emphasize text the reviewer cannot hear (and is the same trap
    // the retired caret had with a removed lead word, CLAUDE.md #9).
    if (isActive && el.state !== 'removed' && el.state !== 'cut') {
      className = `${className} playing`.trim();
    }

    // Synthetic spacers / whitespace (`initial_index < 0` or blank) are not
    // seek/highlight targets — render them for spacing only. Content words carry
    // the index stamps + click-to-seek + the `data-initial-index` the linked
    // cross-language highlight (PLAT-10287) resolves.
    const isContent = el.initial_index >= 0 && !isWhitespace;
    nodes.push(
      <WordLight
        key={pos}
        // PLAT-10266 (Card 6) — the word's LIVE array position, stamped on EVERY
        // rendered word (added OR load-time), NOT just content words. An ADDED
        // `initial_index:-1` word carries no `data-initial-index` / `data-source-uuid`,
        // so this is its only handle: the review's original-column right-click
        // resolves the clicked word by this position, making added/replaced words
        // re-editable. Omitted on whitespace spacers (never an edit target — a
        // click there climbs to the nearest word).
        data-array-position={isWhitespace ? undefined : pos}
        data-index={isContent ? el.initial_index : undefined}
        data-initial-index={isContent ? el.initial_index : undefined}
        // PLAT-10611 (F5) — the source element's stable UUID, so the
        // cross-language link + span-replace original-pane read resolve this
        // original word by identity FIRST (drift-correct); absent on
        // whitespace/synthetic spacers (never a link target).
        data-source-uuid={isContent ? (el.source_uuid ?? el.id) : undefined}
        className={className}
        onClick={isContent ? () => onWordClick(el.initial_index) : undefined}
      >
        {el.value}
      </WordLight>,
    );
  }
  return nodes;
}

/**
 * Is `row` outside the comfortable visible band of `container`? (PLAT-10264 —
 * off-screen-only autoscroll.) The band runs from the top anchor
 * (`PLAYHEAD_TOP_OFFSET_RATIO` of the container height down from the top edge)
 * to the container's bottom edge. The row is "off-screen" when its top sits
 * ABOVE the top anchor or its bottom sits BELOW the container's bottom — i.e.
 * the active row has scrolled out of (or above) the readable area.
 *
 * Why a band, not "any change": the aligned view's blocks can be word-level, so
 * scrolling on EVERY active-block change scrolls on every word — constant motion
 * that is "barely readable". Scrolling only when the row leaves the band keeps
 * the view still while the active row is comfortably visible and nudges it only
 * when it reaches an edge (the prose pane's "scroll when off-screen" feel).
 *
 * Pure (takes only the two rects' relevant fields) so it can be unit-tested
 * without a layout engine. Exported for that reason; not part of the view's API.
 */
export function isRowOutsideBand(
  containerRect: { top: number; height: number },
  rowRect: { top: number; bottom: number },
  topOffsetRatio: number = PLAYHEAD_TOP_OFFSET_RATIO,
): boolean {
  const bandTop = containerRect.top + containerRect.height * topOffsetRatio;
  const bandBottom = containerRect.top + containerRect.height;
  return rowRect.top < bandTop || rowRect.bottom > bandBottom;
}

/**
 * PLAT-10268 (T8) QA polish — what the coverage slot at a translated fragment
 * should render, given its in-flight/verdict state (SPEC AC 4). Pure so the
 * loading→spinner / verdict→badge / neither→nothing precedence is unit-testable
 * without rendering the whole column. Loading WINS over a stale prior verdict: a
 * fresh check is in flight, so the old badge would be misleading. The caller
 * resolves `isLoading` / `verdict` by SOURCE INDEX (BUG B: a re-fix re-tiles the
 * segment so the fragment lead no longer equals the cache/loading key).
 */
export function coverageSlotKind(
  isLoading: boolean,
  verdict: CoverageVerdict | undefined,
): 'loading' | 'badge' | 'none' {
  if (isLoading) return 'loading';
  if (verdict) return 'badge';
  return 'none';
}

/**
 * Split a Changes-card hover's source indices by block coverage: a block whose
 * `originalIndices` are ALL in the hovered set is FULLY covered → its whole
 * row tints (one calm wash — painting every word of a whole-segment rewrite
 * reads as noise); a block only intersected keeps the intersecting indices for
 * the word-level correspondence paint. Pure; exported for unit tests.
 */
export function splitChangeHoverCoverage(
  blocks: readonly Pick<AlignedBlock, 'id' | 'originalIndices'>[],
  hoveredChangeIndices: readonly number[] | null | undefined,
): { fullyCoveredBlockIds: Set<string>; partialIndices: Set<number> } {
  const fullyCoveredBlockIds = new Set<string>();
  const partialIndices = new Set<number>();
  const hovered = new Set(hoveredChangeIndices ?? []);
  if (hovered.size === 0) return { fullyCoveredBlockIds, partialIndices };
  for (const block of blocks) {
    if (block.originalIndices.length === 0) continue;
    let covered = 0;
    for (const idx of block.originalIndices) {
      if (hovered.has(idx)) covered++;
    }
    if (covered === 0) continue;
    if (covered === block.originalIndices.length) {
      fullyCoveredBlockIds.add(block.id);
    } else {
      for (const idx of block.originalIndices) {
        if (hovered.has(idx)) partialIndices.add(idx);
      }
    }
  }
  return { fullyCoveredBlockIds, partialIndices };
}

/** Write a node into one ref, object- or callback-form, no-op when undefined. */
function assignRef<T>(ref: React.Ref<T> | undefined, node: T | null): void {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  // `RefObject.current` is typed read-only; writing it is the whole point of a
  // ref. Reach the mutable slot without a type assertion via an index write.
  (ref as { current: T | null }).current = node;
}

/**
 * Merge two refs into one stable callback ref. Each `Column` must feed BOTH the
 * internal scroll-sync ref (`useScrollSync` reads `.current`) AND the
 * workspace's per-pane highlight ref (the linked-highlight hook scopes to
 * `.current`). A single DOM node, two ref consumers — so this callback ref
 * writes the node into both. The second is optional (the column-ref props are).
 * Memoized on the two ref identities (stable for the view's lifetime), so React
 * doesn't detach/re-attach the node on every render.
 */
function useMergedRef<T>(
  refA: React.Ref<T>,
  refB: React.Ref<T> | undefined,
): (node: T | null) => void {
  return useCallback(
    (node: T | null) => {
      assignRef(refA, node);
      assignRef(refB, node);
    },
    [refA, refB],
  );
}

/**
 * The speaker header / timestamp row that precedes a block's `<Row>` in a
 * column (PLAT-10264 — Option-A reversal). Mirrors the prose
 * `TranscriptionTextInner`: on a speaker change render a `SpeakerHeader`
 * (speaker name + start time), otherwise a `TimestampRow` (start time only);
 * render nothing when there's no time and no new-speaker name. The aligned view
 * has no `onSpeakerClick`, so the speaker label is non-interactive (no click /
 * hover affordance), unlike the prose pane's optionally-clickable label.
 *
 * Returned as a FLAT SIBLING of the block `<Row>` (no wrapper container) so
 * cross-row text selection in the original column is preserved (CLAUDE.md "Text
 * Selection and DOM Structure"). `keyPrefix` disambiguates the two columns'
 * identical block ids (`o-` / `t-`).
 *
 * Exported for unit testing the header/timestamp branching without mounting the
 * store-connected component (same rationale as `renderOriginalWords`). Not part
 * of the view's public API.
 */
export function renderHeaderRow(
  block: AlignedBlock,
  info: BlockHeaderInfo,
  keyPrefix: string,
  // Speaker resolvers. The clip path passes the `useSpeakerLabels` hook's
  // resolvers (real names/colors); both default to the pure label formatters so
  // Content Studio and the existing unit tests render exactly as before.
  resolveSpeakerName: (
    label: string | undefined,
  ) => string = getSpeakerNameFromLabel,
  resolveSpeakerColor: (
    label: string | undefined,
  ) => string = getSpeakerColorFromLabel,
): React.ReactNode {
  const { speakerLabel, startTime, isNewSpeaker } = info;
  if (isNewSpeaker && speakerLabel) {
    return (
      <SpeakerHeader
        key={`${keyPrefix}h-${block.id}`}
        contentEditable={false}
        suppressContentEditableWarning={true}
      >
        <SpeakerLabel $color={resolveSpeakerColor(speakerLabel)}>
          {resolveSpeakerName(speakerLabel)}
        </SpeakerLabel>
        {startTime !== undefined && (
          <SpeakerTime>{formatTime(startTime)}</SpeakerTime>
        )}
      </SpeakerHeader>
    );
  }
  if (startTime !== undefined) {
    return (
      <TimestampRow
        key={`${keyPrefix}t-${block.id}`}
        contentEditable={false}
        suppressContentEditableWarning={true}
      >
        {formatTime(startTime)}
      </TimestampRow>
    );
  }
  return null;
}

export const AlignedBlocksView: React.FC<AlignedBlocksViewProps> = observer(
  function AlignedBlocksView({
    language,
    videoCreator,
    transcriptElements,
    viewMode,
    originalColumnRef,
    translatedColumnRef,
    hideOriginal = false,
    editable = true,
    montagePlaybackOverride,
    onMontageSeek,
    montageReady,
    dubPlayhead,
    coverageByLead,
    dubStateBySegment,
    onRetryDub,
    dubMarkers,
    onMarkerRemove,
    onMarkerEdit,
    loadingCoverageLeads,
    openCoverageLead,
    onCoverageBadgeClick,
    onCoverageClose,
    onCoverageUndo,
    onCoverageFixTranslation,
    onPhraseEditSaved,
    resolveCoverageForPhrase,
    splitControls,
    revealRequest,
    onChangedPhraseHover,
    hoveredChangeIndices,
    referenceLanguage,
    staleLeads,
    onResolveRetranslate,
    onResolveManual,
    staleSelectionLead,
    resolveMenuOpenerRef,
    tmEntriesById,
    tmCollectionNameById,
    onPromoteToTm,
    promotedTmEntryIds,
    onViewTmEntry,
  }) {
    const translationStore = useTranslationStore();
    // Real speaker names/colors via the SAME hook the prose panes use — it
    // reads the story's speaker map from the module cache the Speakers tab +
    // prose panels already populate, and falls back to the pure label
    // formatters ("Speaker N") for unmapped speakers. Both entry paths use it
    // (the earlier Content Studio label-only fallback showed "Speaker 1" even
    // for mapped speakers).
    const { getSpeakerName, getSpeakerColor } = useSpeakerLabels();

    // Right-click "Edit translation" menu + popover state — the SAME
    // view-agnostic state machine the inline prose views use, so editing a
    // phrase here behaves identically (PLAT-10272 edit-parity in the workspace).
    const {
      contextMenu,
      editing,
      editingCurrentValue,
      editingOriginalValue,
      editingIsEdited,
      handleContextMenuRequest,
      handleMenuEdit,
      handleMenuClose,
      handleOpenEditor,
      handleSave,
      handleCancel,
      handleRevert,
    } = usePhraseEditing(language, editable);

    // PLAT-11650 — "click a translated phrase and type": the in-place field.
    // Separate from the popover state above; the field's Enter goes through
    // the SAME `handleSave` so the change log, coverage badge, stale flags
    // and TM marks see one more ordinary phrase edit.
    const { inlineEdit, inlineCurrentValue, openInlineEdit, cancelInlineEdit } =
      useInlinePhraseEdit(language, editable);
    // PLAT-11650 — the phrase under which the "Double-click to edit" hint sits,
    // after a single click that could have been an edit. Shows until this
    // browser has learned the gesture (a double-click or the pencil used once),
    // at most `HINT_MAX_PER_LOAD` times per page load.
    const [editHintAnchor, setEditHintAnchor] = useState<HTMLElement | null>(
      null,
    );
    const hintShowsThisLoadRef = useRef(0);
    // PLAT-11650 — the one hover pencil: which editable phrase the pointer is
    // resting on. Delayed in so a sweep across a paragraph does not flash a
    // pencil per phrase; delayed out so the pointer can cross the gap onto the
    // pencil itself (the hover bridge, as the coverage badge does).
    const [hoverPencil, setHoverPencil] = useState<{
      phrase: TranslationPhrase;
      blockId: string;
      lead: number;
      anchorElement: HTMLElement;
    } | null>(null);
    const pencilShowTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const pencilHideTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const pointerOnPencilRef = useRef(false);
    const clearPencilTimers = useCallback(() => {
      if (pencilShowTimerRef.current) clearTimeout(pencilShowTimerRef.current);
      if (pencilHideTimerRef.current) clearTimeout(pencilHideTimerRef.current);
      pencilShowTimerRef.current = undefined;
      pencilHideTimerRef.current = undefined;
    }, []);
    const schedulePencilHide = useCallback(() => {
      if (pencilHideTimerRef.current) clearTimeout(pencilHideTimerRef.current);
      pencilHideTimerRef.current = setTimeout(() => {
        pencilHideTimerRef.current = undefined;
        if (!pointerOnPencilRef.current) setHoverPencil(null);
      }, 150);
    }, []);
    const handlePencilHoverChange = useCallback(
      (inside: boolean) => {
        pointerOnPencilRef.current = inside;
        if (inside) {
          if (pencilHideTimerRef.current) {
            clearTimeout(pencilHideTimerRef.current);
            pencilHideTimerRef.current = undefined;
          }
        } else {
          schedulePencilHide();
        }
      },
      [schedulePencilHide],
    );
    useEffect(() => clearPencilTimers, [clearPencilTimers]);
    // Learning the gesture, by either route, ends the hint for good.
    const learnDoubleClickEdit = useCallback(() => {
      markDoubleClickEditLearned();
      setEditHintAnchor(null);
    }, []);
    const handleInlineCommit = useCallback(
      (phrase: TranslationPhrase, draft: string) => {
        const current = inlineCurrentValue;
        const value = normalizeTypedTranslation(draft);
        // Deleting every word and pressing Enter commits a REMOVAL: the save
        // below is the same `handleSave(phrase, '')` the menu's "Remove
        // Translation" makes, so an emptied phrase takes the removal storage
        // shape (PLAT-11340 f/u) and renders its struck machine baseline. It
        // used to bail here, which silently discarded the deletion the red-line
        // bubble had just offered to save.
        // A no-op still never touches the store or analytics — the store's own
        // gate would drop it silently otherwise.
        if (normalizeForCompare(value) === normalizeForCompare(current)) {
          cancelInlineEdit();
          return;
        }
        handleSave(phrase, value);
        onPhraseEditSaved?.(phrase.originalElementIds ?? []);
        const hunks = summarizeRedline(diffWordsForRedline(current, value));
        trackTranslationReview_InlineEditCommitted({
          story_id: videoCreator.storyId ?? undefined,
          language,
          kind: classifyInlineEdit(hunks),
          words_before: countWords(current),
          words_after: countWords(value),
        });
        cancelInlineEdit();
      },
      [
        inlineCurrentValue,
        handleSave,
        onPhraseEditSaved,
        cancelInlineEdit,
        videoCreator,
        language,
      ],
    );

    // PLAT-11678 — a revert changes the spoken text back just as a save does,
    // so every Revert surface (the red-line bubble, the editor popover, the
    // menu's Restore) must reach the coverage re-check + auto re-dub the same
    // way a commit does. `handleRevert` alone only rewrites the store; without
    // this the dub would silently stay voiced from the reverted-away text.
    const handleRevertAndSync = useCallback(
      (phrase: TranslationPhrase) => {
        handleRevert(phrase);
        onPhraseEditSaved?.(phrase.originalElementIds ?? []);
      },
      [handleRevert, onPhraseEditSaved],
    );

    // PLAT-10266 (Card 2) — the inline resolve menu for a STALE (amber) fragment.
    // Opened by right-clicking a stale fragment (see the translated word's
    // onContextMenu below); anchored to that word. Review-scoped — only present
    // when `onResolveRetranslate` is wired (whole-story host).
    const [resolveMenu, setResolveMenu] = useState<{
      lead: number;
      anchorElement: HTMLElement;
    } | null>(null);

    // PLAT-10266 (Card R2) — did the HIGHLIGHT path open the menu that is currently open?
    // Only a highlight-opened menu may be auto-closed when the selection stops being a
    // single stale fragment; a menu the reviewer opened by right-clicking must stay put.
    const resolveMenuFromHighlightRef = useRef(false);

    // PLAT-10266 (Card R2) — let the workspace drive that menu from the HIGHLIGHT path.
    // The selection is owned by `useSpanReplace` up in the workspace, but this menu's
    // state is local here, so the opener is handed up by ref. No dep array: reassigned
    // each render so it always closes over current state, and cleared on unmount — the
    // same idiom `InlineSpanReplaceLayer` uses for its `openEditorRef`.
    //
    // It also CLOSES (called with null). A drag passes through intermediate selections:
    // starting on a stale word it is briefly a single stale fragment — which opens the
    // menu — and only becomes mixed as the drag extends. Without the close, that
    // mid-drag menu stayed open over a mixed selection, showing BOTH it and the span
    // trigger. Caught live by the mixed-selection regression probe.
    useEffect(() => {
      if (!resolveMenuOpenerRef) return;
      resolveMenuOpenerRef.current = hit => {
        // Guard the same way the right-click path does: no resolve affordance exists
        // unless the host wired it (omitted on the clip host and prose homes, AC6).
        if (!onResolveRetranslate) return;
        if (hit) {
          resolveMenuFromHighlightRef.current = true;
          setResolveMenu({ lead: hit.lead, anchorElement: hit.anchorElement });
          return;
        }
        if (!resolveMenuFromHighlightRef.current) return;
        resolveMenuFromHighlightRef.current = false;
        setResolveMenu(null);
      };
      return () => {
        resolveMenuOpenerRef.current = null;
      };
    });

    // PLAT-10268 (T8) — Atom 6. The clicked badge's DOM element, so the coverage
    // popover (mounted at the Grid level, like the editor popover) anchors to the
    // fragment. Which lead is OPEN lives in the workspace (`openCoverageLead`, AC
    // 1); this is only the transient anchor rect. Cleared when the workspace
    // closes the popover (`openCoverageLead` back to null).
    const [coverageAnchorEl, setCoverageAnchorEl] =
      useState<HTMLElement | null>(null);
    const handleBadgeClick = useCallback(
      (lead: string, anchorElement: HTMLElement) => {
        setCoverageAnchorEl(anchorElement);
        onCoverageBadgeClick?.(lead);
      },
      [onCoverageBadgeClick],
    );
    // Stable close handler for the popover (PLAT-10268 review findings 3 & 4).
    // A fresh arrow each render made `useOutsideAlerter` re-subscribe its
    // document listener on every popover render (finding 3); a stable identity
    // fixes that. It also clears the transient badge anchor on close so a stale
    // rect isn't retained (finding 4 — benign, tidied here).
    const handleCoveragePopoverClose = useCallback(() => {
      setCoverageAnchorEl(null);
      onCoverageClose?.();
    }, [onCoverageClose]);

    // ---- Hover intent (PLAT-10268, Denys 2026-07-14) -----------------------
    // HOVER is the primary way into the score's details. Two timers make that
    // usable rather than twitchy:
    //  * OPEN delay — sweeping the pointer across a column of scores must not
    //    flash a popover per badge; the pointer has to settle.
    //  * CLOSE delay — the popover carries real controls (Undo, Split), so the
    //    pointer must be able to travel from the badge INTO the popover across
    //    the gap between them. Without this grace period those buttons are
    //    unreachable: the popover would vanish the moment the badge is left.
    // The popover cancels the pending close on its own mouse-enter (the bridge),
    // and re-arms it on leave. Click, Escape and click-outside all still work —
    // hover is added, nothing is taken away.
    const HOVER_OPEN_MS = 120;
    const HOVER_CLOSE_MS = 260;
    const hoverOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearHoverTimers = useCallback(() => {
      if (hoverOpenTimer.current) clearTimeout(hoverOpenTimer.current);
      if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
      hoverOpenTimer.current = null;
      hoverCloseTimer.current = null;
    }, []);
    // Never leave a timer running past unmount (it would setState on a dead tree).
    useEffect(() => clearHoverTimers, [clearHoverTimers]);

    const handleBadgeHoverOpen = useCallback(
      (lead: string, anchorElement: HTMLElement) => {
        clearHoverTimers();
        hoverOpenTimer.current = setTimeout(() => {
          setCoverageAnchorEl(anchorElement);
          onCoverageBadgeClick?.(lead);
        }, HOVER_OPEN_MS);
      },
      [clearHoverTimers, onCoverageBadgeClick],
    );
    const handleBadgeHoverClose = useCallback(() => {
      clearHoverTimers();
      hoverCloseTimer.current = setTimeout(
        handleCoveragePopoverClose,
        HOVER_CLOSE_MS,
      );
    }, [clearHoverTimers, handleCoveragePopoverClose]);
    // The bridge: pointer reached the popover — keep it open.
    const handlePopoverHoverEnter = useCallback(() => {
      clearHoverTimers();
    }, [clearHoverTimers]);
    const handlePopoverHoverLeave = useCallback(() => {
      clearHoverTimers();
      hoverCloseTimer.current = setTimeout(
        handleCoveragePopoverClose,
        HOVER_CLOSE_MS,
      );
    }, [clearHoverTimers, handleCoveragePopoverClose]);

    // Elements as the modal receives them, now sourced ONCE by the sole parent
    // (`TranslationReviewWorkspace`) and passed in as `transcriptElements`
    // (PLAT-10464 Atom 1): the clip-proxied `getFullTranscript` in the
    // clip-preview entry (carries out-of-clip content as `RemovedTranscriptClip`
    // segments), or the plain story transcript in Content Studio.
    const originalElements = transcriptElements;

    // PLAT-10209 H-A — clip-scoped gate. `sourceElements` is a clip-LOCAL,
    // montage-ordered subset in the clip-preview entry, or `originalElements`
    // unchanged in Content Studio (`clipScoped: false`). Everything below renders
    // from `sourceElements`, so the columns show only the clip's content in clip
    // order. See `deriveClipScopedElements`.
    const { sourceElements, clipScoped } = useMemo(
      () => deriveClipScopedElements(originalElements),
      [originalElements],
    );

    // The edit popover's source text. Resolved once here (it walks every base
    // element) because two of its props need it: the read-only "Original" row
    // and the TM promote seed (PLAT-10852).
    const editingSourceText = useMemo(
      () =>
        editing ? resolveSourceText(editing.phrase, sourceElements ?? []) : '',
      [editing, sourceElements],
    );

    // Highlight clock: on the clip path, the RAW montage clock (parity with the
    // clip preview, PLAT-10464) — paired with the elements' own `ts`/`end_ts`,
    // which are already the packed montage clock, so the active-phrase pick
    // compares montage-vs-montage and no longer converts montage→recording (that
    // map drift landed the highlight ~one paragraph early on edited clips).
    // Otherwise the
    // shared `videoCreator.time` (read inside the `observer` so each tick
    // re-highlights — Content Studio). `isPlaying` follows the override on the
    // clip path, else `videoCreator`. Both are read in render so they stay stable
    // deps for the autoscroll effects below.
    const currentTime = montagePlaybackOverride
      ? // Highlight only once the montage timing snapshot has arrived and been
        // baked onto the elements (`montageReady`). Before then, `undefined`
        // yields no active phrase (better than matching the raw montage time
        // against recording-clock timings — the very mismatch this fix removes).
        // The renderer opens paused at the start, so the window is brief and
        // silent.
        montageReady
        ? montagePlaybackOverride.montageTime
        : undefined
      : videoCreator.time;
    const isPlaying = montagePlaybackOverride
      ? montagePlaybackOverride.isPlaying
      : videoCreator.isPlaying;

    // Both paths use the hook's resolved real names/colors (label-formatter
    // fallback inside the hook). `renderHeaderRow` is module-level (it can't
    // close over the component body), so the resolvers are passed into it.
    const resolveSpeakerName = getSpeakerName;
    const resolveSpeakerColor = getSpeakerColor;

    // Active unit (playing fragment's lead) + the clip-scope descriptor
    // (`inScope`), both from the SAME source array the columns render. Both
    // paths pass `includeOutOfScope: false`, so the hook drops cut/removed from
    // the paragraph spine and fully-struck blocks never enter the layout. On the
    // clip path the in-line struck words that survive inside a kept block's span
    // (mid-sentence cuts interleaved between kept words) are filtered out in
    // `renderOriginalWords` instead — see its `clipScoped` redline gate. Content
    // Studio whole-story is unchanged (it was already `false`). `groups` +
    // `phrasesByGroup` drive Paragraph mode's rows.
    const { currentLead, currentLeadUuid, inScope, groups, phrasesByGroup } =
      useTranslatedRender(language, sourceElements, currentTime, {
        includeOutOfScope: false,
        // PLAT-10266 (Card 7, §B) — opt into the vanished-source backfill so a
        // "Replace Term" edit's deleted-source phrase re-appears in Paragraph
        // mode (amber). WHOLE-STORY ONLY (`!clipScoped`): the editable-original
        // edit is gated to the whole-story review, and on a clip an out-of-clip
        // phrase's ids are legitimately absent from the clip-local array — so
        // the flag must stay OFF there to avoid resurfacing out-of-clip rows.
        backfillVanishedSourcePhrases: !clipScoped,
      });

    // Montage start per source `initial_index`, read off the elements' `ts`
    // (already the packed montage clock on the clip path — PLAT-10464). Drives
    // the clip-path click-to-seek below. Spacer elements (`initial_index` -1)
    // and cut/removed words (`ts` null) never seed a seek target.
    const montageTsByInitialIndex = useMemo(() => {
      const map = new Map<number, number | undefined>();
      if (sourceElements) {
        sourceElements.forEach(el => {
          if (el.initial_index >= 0) {
            map.set(
              el.initial_index,
              typeof el.ts === 'number' ? el.ts : undefined,
            );
          }
        });
      }
      return map;
    }, [sourceElements]);

    // Click-word-to-seek: move the playhead to the clicked element's `ts`.
    // Same shared helper `Transcript.tsx`'s translated `seekToIndex` uses, so
    // the aligned view's seek behavior is identical to the prose pane's.
    // Left column passes the clicked word's own `initial_index`; the right
    // column passes the block's `lead` (its first spoken word), matching the
    // translated prose pane where the phrase lead carries the click target.
    // PLAT-11482 — which source indices a translated word may rest on. Same
    // rule the cursor's own stepping uses (`buildPhraseStepIndices`): a kept
    // TEXT element, since punctuation and whitespace carry no usable `ts` and
    // would make click-to-seek a silent no-op.
    const isStoppableIndex = useMemo(() => {
      const stoppable = new Set<number>();
      for (const el of sourceElements) {
        if (
          el.type === 'text' &&
          el.state !== 'removed' &&
          el.state !== 'cut'
        ) {
          stoppable.add(el.initial_index);
        }
      }
      return (index: number) => stoppable.has(index);
    }, [sourceElements]);

    const seek = useCallback(
      (initialIndex: number) => {
        // Clip review modal: seek the MONTAGE renderer (the visible player here)
        // instead of the shared `videoCreator` (whose renderer is hidden/
        // suppressed in this modal). The clicked word's montage start comes
        // straight from the element's `ts` (already the montage clock —
        // PLAT-10464) — no recording→montage conversion. A missing value
        // (cut/removed word not in the montage — `ts` null) is a no-op. The
        // highlight then follows from the renderer's `onTimeChange`. Content
        // Studio (no override / no callback) is unchanged.
        if (montagePlaybackOverride && montageReady && onMontageSeek) {
          const montageTs = montageTsByInitialIndex.get(initialIndex);
          if (montageTs == null) return;
          onMontageSeek(montageTs);
          return;
        }
        seekToInitialIndex(videoCreator, sourceElements, initialIndex);
      },
      [
        montagePlaybackOverride,
        montageReady,
        montageTsByInitialIndex,
        onMontageSeek,
        videoCreator,
        sourceElements,
      ],
    );

    // Map each content element's `initial_index` -> its POSITION in
    // `sourceElements`. The per-block render slices the array by position (not by
    // an initial_index walk); the array slice is what includes the inter-word
    // whitespace/punctuation needed for spacing — see `renderOriginalWords`.
    //
    // In clip-scoped mode `sourceElements` is already the clip-LOCAL,
    // montage-ordered array (out-of-clip dropped by `deriveClipScopedElements`),
    // so these positions are clip-local — a block's index range slices its own
    // montage-contiguous run, never a story-order span bridging a montage cut.
    const posByInitialIndex = useMemo(() => {
      const map = new Map<number, number>();
      if (sourceElements) {
        sourceElements.forEach((el, pos) => {
          if (el.initial_index >= 0) map.set(el.initial_index, pos);
        });
      }
      return map;
    }, [sourceElements]);

    // Stable element UUID (`source_uuid ?? id`) -> position: the drift-correct
    // twin of `posByInitialIndex` (PLAT-10611). `renderOriginalWords` resolves a
    // block's slice bounds + its foreign-word membership by UUID FIRST off this
    // map, so a reused/reordered clip whose `initial_index` shifted from story
    // space still tiles each segment/fragment to its own words. Built once per
    // `sourceElements`, like its integer twin.
    const posBySourceUuid = useMemo(() => {
      const map = new Map<string, number>();
      if (sourceElements) {
        // PLAT-11753 — register under EVERY key a stored blob may have used
        // (`stableKeysOf`: provenance first, then the token's own id). A
        // translation produced after a "Replace Term" edit records the MINTED
        // `id`, so keying by `source_uuid` alone left the replacement word
        // unresolvable: a block whose OTHER id still resolved then collapsed its
        // window onto that one element and rendered a bare "." (PLAT-11682).
        // First writer wins, so a recycled minted id can never shadow a
        // provenance key — see `stableKeysOf`.
        sourceElements.forEach((el, pos) => {
          for (const key of stableKeysOf(el)) {
            if (!map.has(key)) map.set(key, pos);
          }
        });
      }
      return map;
    }, [sourceElements]);

    // Block model for the chosen granularity (PLAT-10209 H-A). The translation
    // store is NOT clip-proxied — `getSegments` / `phrases` describe the WHOLE
    // story — so the block set must be scoped to the clip. In clip-scoped mode
    // the scope is a BARE `Set` of the clip-local `inScope.activeIndices`: pure
    // intersection, no `[minIdx, maxIdx]` range (a global range would leak across
    // montage cut-gaps). Because the hook runs with `includeOutOfScope: false`,
    // `activeIndices` carries only kept words, so fully-struck blocks don't
    // intersect it and drop out of the layout; any in-line struck words that
    // survive inside a kept block's span are hidden in `renderOriginalWords` via
    // its `clipScoped` redline gate. Content Studio passes the full `inScope`
    // descriptor (range applies) — unchanged. Paragraph mode preserves montage
    // order via `clipScoped`.
    const segments = translationStore.getSegments(language);
    const translation = translationStore.getTranslation(language);

    // PLAT-10852 — the promote/link key for the fragment being edited; an
    // override resolves back to its authored span so this agrees with the
    // Changes card. See `overlayPromoteKey`.
    const editingFragmentKey = editing
      ? overlayPromoteKey(editing.phrase, translation?.parentPhrases)
      : '';

    // The TM entry behind the fragment being edited, if any (PLAT-10852).
    const editingTmEntry = editing
      ? resolveFragmentTmEntry(
          editingFragmentKey,
          editing.phrase.tmEntryIds,
          tmEntriesById,
          promotedTmEntryIds,
        )
      : null;
    const blocks = useMemo(() => {
      if (viewMode === 'paragraph') {
        // One aligned row per paragraph group (PLAT-10288). The groups come from
        // `useTranslatedRender` over `sourceElements`, so in clip-scoped mode they
        // are clip-local and montage-ordered; `clipScoped` preserves that order.
        // PLAT-11717 — `sourceElements` is what the builder stamps each block's
        // `arraySpan` against: the groups' own element positions index the hook's
        // `filteredElements`, which is NOT the array rendered below.
        return buildParagraphBlocks(
          groups,
          phrasesByGroup,
          clipScoped,
          sourceElements,
        );
      }
      const activePhrases =
        translation?.phrases.filter(p => p.active !== false) ?? [];
      // PLAT-10689 — run-grouped Segment/Fragment layout. On a montage-reordered
      // clip a segment's fragments are scattered across playback, so the
      // one-block-per-segment layout below showed a split segment's later
      // fragments too early (and pushed the interposing segments down). Grouping
      // the clip's content into contiguous playback runs by owning segment /
      // phrase renders the original in playback order (a split segment becomes
      // multiple rows). Flip `USE_RUN_GROUPED_BLOCKS` to `false` to restore the
      // prior one-block-per-segment behavior (`buildAlignedBlocks` + renderPos
      // sort). Clip path only — Content Studio (array order == story order) has
      // no scatter, so the legacy path already yields one contiguous row per
      // segment.
      if (USE_RUN_GROUPED_BLOCKS && clipScoped) {
        return buildRunGroupedBlocks(
          viewMode,
          segments,
          activePhrases,
          sourceElements ?? [],
        );
      }
      // PLAT-10611 (F5, F3-carry) — the clip path now passes a DESCRIPTOR
      // carrying `activeUuids` (NOT a bare `Set<number>`), so `blockInScope`
      // intersects the clip scope by STABLE id FIRST (drift-correct on a
      // reordered clip) and falls back to the integer `activeIndices`. It still
      // omits the `[minIdx, maxIdx]` band (a global range leaks across montage
      // cut-gaps). Content Studio passes the full `inScope` (band applies) —
      // unchanged.
      const scope = clipScoped
        ? {
            activeIndices: inScope.activeIndices,
            activeUuids: inScope.activeUuids,
          }
        : inScope;
      const built = buildAlignedBlocks(
        viewMode,
        segments,
        activePhrases,
        scope,
      );
      if (!clipScoped) return built;
      // Reorder by PLAYBACK position, not `lead` (source `initial_index`).
      // `buildAlignedBlocks` sorts by `lead`, but on a montage-reordered clip
      // source-index order ≠ play order, so a moved segment/phrase sorted into its
      // original story slot instead of its clip slot — both columns then showed
      // the reordered phrase in original order. `sourceElements` is in
      // playback/recording order, so a block's smallest array position is its
      // playback rank; sorting by it matches the translated / Paragraph path
      // (already render-position ordered) and the left column's own array-order
      // render. Clip-only: Content Studio (array order == index order) keeps the
      // `lead` sort untouched.
      const renderPos = (b: AlignedBlock): number => {
        let min = Infinity;
        for (const idx of b.originalIndices) {
          const pos = posByInitialIndex.get(idx);
          if (pos !== undefined && pos < min) min = pos;
        }
        return min;
      };
      return built.sort((a, b) => renderPos(a) - renderPos(b));
    }, [
      viewMode,
      segments,
      translation,
      inScope,
      groups,
      phrasesByGroup,
      clipScoped,
      posByInitialIndex,
      sourceElements,
    ]);

    // PLAT-10268 (QA round 2) — the translated column's phrases in RENDER
    // order (blocks top-to-bottom, fragments left-to-right). A cache/loading
    // entry overlaps EVERY fragment of the span it covers; the coverage slot
    // must render exactly once per entry, on the LAST overlapping phrase (the
    // end of the analyzed span). Flattened once here so the per-phrase slot
    // decision can resolve that last phrase without re-walking the blocks.
    const renderedTranslatedPhrases = useMemo(
      () =>
        blocks.flatMap(b => (b.translatedPhrases ?? []).map(tp => tp.phrase)),
      [blocks],
    );

    // PLAT-10268 — the coverage indicators each block should show, resolved
    // ONCE for both columns. The translated column renders them; the original
    // column emits an empty row of the same align key so the height-match pass
    // keeps the two columns row-aligned (the coverage row lives OUTSIDE `Row`,
    // directly under the timestamp, so without a partner the right column would
    // grow and everything below would drift).
    const coverageIndicatorsByBlock = useMemo(() => {
      const byBlock = new Map<
        string,
        {
          leadUuid: string;
          kind: ReturnType<typeof coverageSlotKind>;
          verdict: CoverageVerdict | undefined;
        }[]
      >();
      for (const block of blocks) {
        const indicators = (block.translatedPhrases ?? []).flatMap(phrase => {
          const ph = phrase.phrase;
          // Stable-ID identity: a fragment carrying no ids (a stale translation)
          // never overlaps a cached span and never gets a badge (no index path).
          const phElementIds = ph.originalElementIds ?? [];
          const leadUuid = ph.originalElementIds?.[0];
          const coverageEntry = coverageByLead
            ? entryForPhrase(
                coverageByLead,
                phElementIds,
                translation?.phrases ?? [],
              )
            : undefined;
          const coverageVerdict =
            coverageEntry &&
            isLastOverlappingPhrase(
              coverageEntry.originalElementIds,
              ph,
              renderedTranslatedPhrases,
            )
              ? coverageEntry.verdict
              : undefined;
          const kind = coverageSlotKind(
            isCoverageLoadingForPhrase(loadingCoverageLeads, phElementIds) &&
              isLastOverlappingPhrase(
                phElementIds.filter(
                  id => loadingCoverageLeads?.has(id) ?? false,
                ),
                ph,
                renderedTranslatedPhrases,
              ),
            coverageVerdict,
          );
          return kind === 'none' || leadUuid === undefined
            ? []
            : [{ leadUuid, kind, verdict: coverageVerdict }];
        });
        if (indicators.length > 0) byBlock.set(block.id, indicators);
      }
      return byBlock;
    }, [
      blocks,
      coverageByLead,
      translation,
      renderedTranslatedPhrases,
      loadingCoverageLeads,
    ]);

    // A coverage badge/spinner mounts ASYNCHRONOUSLY (when the check resolves),
    // changing a row's height without changing `blocks` or the column width —
    // the only two things the height-match pass below re-runs on. Measured: the
    // columns drift by ~26px the moment a badge appears. So the pass also keys
    // on this signal. It is a STRING, not the Map: the Map is a fresh identity
    // every render and would re-run the pass (and thrash layout) constantly.
    // PLAT-11678 — which re-dub statuses each block carries, one per distinct
    // segment. A block's phrases key their segment the way `getSegments` does
    // (`parentId`, else the lead element id), so a Paragraph block spanning
    // several sentences can show several. Empty map when the feature is off.
    const dubStatusesByBlock = useMemo(() => {
      const byBlock = new Map<
        string,
        { segmentId: string; state: AutoRedubSegmentState }[]
      >();
      if (!dubStateBySegment || dubStateBySegment.size === 0) return byBlock;
      for (const block of blocks) {
        const seen = new Set<string>();
        const statuses: { segmentId: string; state: AutoRedubSegmentState }[] =
          [];
        for (const tp of block.translatedPhrases ?? []) {
          const segmentId =
            tp.phrase.parentId ?? tp.phrase.originalElementIds?.[0];
          if (!segmentId || seen.has(segmentId)) continue;
          seen.add(segmentId);
          const state = dubStateBySegment.get(segmentId);
          if (state) statuses.push({ segmentId, state });
        }
        if (statuses.length > 0) byBlock.set(block.id, statuses);
      }
      return byBlock;
    }, [blocks, dubStateBySegment]);

    // PLAT-12034 — where each line's delivery markers sit on the fragments
    // this view draws: the line's text as it reads now and its fragments'
    // ranges in it (the same join the dub playhead uses), then each marker to
    // the fragment holding the word it precedes. Keyed by fragment key.
    const markerLineRanges = useMemo(
      () =>
        dubMarkers && dubMarkers.size > 0
          ? lineFragmentRanges(translation?.phrases ?? [], transcriptElements)
          : null,
      [dubMarkers, translation?.phrases, transcriptElements],
    );
    const markersByFragment = useMemo(() => {
      const out = new Map<string, PlacedMarker[]>();
      if (!dubMarkers || !markerLineRanges) return out;
      const ranges = markerLineRanges;
      for (const line of dubMarkers.values()) {
        const now = ranges.get(line.lineId);
        if (!now) continue;
        const placed = placeMarkers(line, {
          text: now.text,
          fragments: now.fragments.map(range => ({
            key: reviewFragmentKey(range.phrase),
            start: range.start,
            end: range.end,
          })),
        });
        for (const [key, list] of placed) {
          out.set(key, [...(out.get(key) ?? []), ...list]);
        }
      }
      return out;
    }, [dubMarkers, markerLineRanges]);
    // Lines edited since they were voiced: their markers sit over words the
    // line no longer has, so a removal rebuilt from them would voice the old
    // words. Their chips offer Edit Markers… only.
    const editedMarkerLines = useMemo(() => {
      const out = new Set<string>();
      if (!dubMarkers || !markerLineRanges) return out;
      for (const line of dubMarkers.values()) {
        const now = markerLineRanges.get(line.lineId);
        if (now && !markersFitText(line, now.text)) out.add(line.lineId);
      }
      return out;
    }, [dubMarkers, markerLineRanges]);

    const coverageAlignSignal = useMemo(
      () =>
        [
          ...[...coverageIndicatorsByBlock.entries()].map(
            ([id, inds]) => `${id}:${inds.map(i => i.kind).join(',')}`,
          ),
          // A dub status row appears and leaves asynchronously too, and moves
          // the row's height the same way a coverage badge does.
          ...[...dubStatusesByBlock.entries()].map(
            ([id, sts]) =>
              `dub:${id}:${sts.map(s => s.state.status).join(',')}`,
          ),
          // So do marker chips, which lift a line's height when they land.
          ...[...markersByFragment.entries()].map(
            ([key, ms]) => `mk:${key}:${ms.map(m => m.tag).join(',')}`,
          ),
        ].join('|'),
      [coverageIndicatorsByBlock, dubStatusesByBlock, markersByFragment],
    );

    // Previous-value source for the changed-phrase hover (PLAT-10265) on
    // span-replace OVERRIDE fragments: id → the machine text the override
    // replaced. Built from ALL phrases (retired ones carry it); rebuilt only
    // when the translation data changes.
    const overrideOriginalById = useMemo(
      () => buildOverrideOriginalMap(translation?.phrases ?? []),
      [translation],
    );

    // Block-aware split of the Changes-card hover (workspace
    // `hoveredChangeIndices`): a block FULLY covered by the hovered change
    // tints its whole row (one calm wash — painting every word of a
    // whole-segment rewrite reads as noise); only PARTIALLY covered blocks
    // keep the word-level correspondence paint.
    const changeHoverCoverage = useMemo(
      () => splitChangeHoverCoverage(blocks, hoveredChangeIndices),
      [blocks, hoveredChangeIndices],
    );

    // Proxy REFERENCE column (PLAT-10655): when a reference language is
    // selected, project ITS active phrases into the reviewed language's blocks
    // (rows stay tiled by the reviewed segmentation). Null when the original
    // is the reference — the left column then renders source words as today.
    const proxyTranslation = referenceLanguage
      ? translationStore.getTranslation(referenceLanguage)
      : null;
    const proxyPhrasesByBlock = useMemo(() => {
      if (!proxyTranslation) return null;
      const active = proxyTranslation.phrases.filter(p => p.active !== false);
      return projectPhrasesIntoBlocks(blocks, active);
    }, [blocks, proxyTranslation]);

    // Source element keyed by its `initial_index` — feeds the kept-word time
    // anchor derivation and the per-block speaker/paragraph lookups below.
    const elementByInitialIndex = useMemo(() => {
      const map = new Map<number, TranscriptElement>();
      if (sourceElements) {
        for (const el of sourceElements) {
          if (el.initial_index >= 0) map.set(el.initial_index, el);
        }
      }
      return map;
    }, [sourceElements]);

    // PLAT-11765 — "has the playhead reached this fragment?". The translated
    // column has no per-word timing of its own (a `TranslationPhrase` is a
    // fragment, sometimes a whole sentence), so the fragment is the finest it
    // can go: it brightens once the playhead reaches the FIRST source word it
    // covers, which keeps it from reading as unread while it is being spoken. Cached per phrase object in a
    // WeakMap that is thrown away whenever the source elements change, so a
    // playhead tick only compares numbers.
    const isPhraseRead = useMemo(() => {
      const startTsByPhrase = new WeakMap<TranslationPhrase, number | null>();
      const resolveStartTs = (phrase: TranslationPhrase): number | null => {
        const cached = startTsByPhrase.get(phrase);
        if (cached !== undefined) return cached;
        let startTs: number | null = null;
        for (const idx of phrase.originalIndices) {
          const el = elementByInitialIndex.get(idx);
          if (!el || el.type !== 'text') continue;
          if (el.state === 'removed' || el.state === 'cut') continue;
          if (
            typeof el.ts === 'number' &&
            (startTs === null || el.ts < startTs)
          ) {
            startTs = el.ts;
          }
        }
        startTsByPhrase.set(phrase, startTs);
        return startTs;
      };
      return (phrase: TranslationPhrase, at: number | undefined): boolean => {
        if (at === undefined) return false;
        const startTs = resolveStartTs(phrase);
        return startTs !== null && at >= startTs;
      };
    }, [elementByInitialIndex]);

    // PLAT-10611 (F5, P5/P6) — the stable UUID of the source element at a lead
    // index (`source_uuid ?? id`), stamped as `data-source-uuid` on the
    // translated column's word/block spans beside `data-initial-index={lead}`.
    // Derived from the SAME lead the span stamps, so the cross-language link
    // (whose per-phrase `leadUuid` is the element at min(originalIndices)) and
    // the active-phrase scroll (`currentLeadUuid`) resolve these spans by
    // identity FIRST; `undefined` when the lead element has no id.
    const leadSourceUuid = useCallback(
      (leadIndex: number): string | undefined => {
        const el = elementByInitialIndex.get(leadIndex);
        return el ? (el.source_uuid ?? el.id) : undefined;
      },
      [elementByInitialIndex],
    );

    // PLAT-11906 — while the review plays a dub, how far the reader has got by
    // the dub's own clock. The map ties each dub line to its fragments and
    // source words once per dub and translation; the position is read each
    // tick from the same clock as the English highlight. The full transcript
    // gives each fragment its order in its line, as the dub captions use it.
    const dubMap = useMemo(
      () =>
        dubPlayhead
          ? buildReviewDubMap(
              dubPlayhead,
              translation?.phrases ?? [],
              transcriptElements,
            )
          : null,
      [dubPlayhead, translation?.phrases, transcriptElements],
    );
    const dubPosition: ReviewDubPosition | null =
      dubMap && currentTime !== undefined
        ? readReviewDubPlayhead(dubMap, currentTime)
        : null;
    const dubActiveLineId = dubPosition?.activeLineId ?? null;
    const dubActiveFragmentKey = dubPosition?.activeFragmentKey ?? null;
    // The rows the playing dub line covers (all of them are "playing", the
    // segment-level rule), and the row and phrase the reader is at, which the
    // active row and the follow-scroll use in place of the English ones.
    const dubActive = useMemo(() => {
      if (!dubMap) return null;
      const playingBlockIds = new Set<string>();
      let activeBlockId: string | null = null;
      let lead: number | null = null;
      for (const block of blocks) {
        for (const unit of block.translatedPhrases ?? []) {
          const key = reviewFragmentKey(unit.phrase);
          if (
            dubActiveLineId !== null &&
            dubMap.lineOfFragment.get(key) === dubActiveLineId
          ) {
            playingBlockIds.add(block.id);
          }
          if (key === dubActiveFragmentKey && activeBlockId === null) {
            activeBlockId = block.id;
            lead = unit.lead;
          }
        }
      }
      return { playingBlockIds, activeBlockId, lead };
    }, [dubMap, blocks, dubActiveLineId, dubActiveFragmentKey]);
    const followLead = dubActive ? dubActive.lead : currentLead;
    const followLeadUuid = dubActive
      ? dubActive.lead !== null
        ? (leadSourceUuid(dubActive.lead) ?? null)
        : null
      : currentLeadUuid;

    // Per-block speaker header / timestamp info (PLAT-10264 — Option-A
    // reversal), in block render order. Two robustness fixes layered on the
    // first cut:
    //
    //  1. Start time + caret anchor from the FIRST KEPT word, not the lead
    //     (CLAUDE.md #9). `deriveBlockTimeAnchor` scans the block's source words
    //     for the earliest kept word with a numeric `ts`; reading the lead's
    //     `ts` blindly drops the timestamp + lands the caret on a struck word
    //     when the lead is removed/cut.
    //  2. Section break (`isNewSpeaker`) on a true speaker change only, keyed on
    //     the RESOLVED display name (not the raw `speaker_label`) — matching the
    //     prose pane's speaker-only name toggle. `isNewSpeaker` gates the FULL
    //     speaker header (name + time) vs. a bare `TimestampRow` (time only);
    //     every other block shows just its timestamp. Two traps avoided: (a) an
    //     earlier cut broke on `paragraph_id`, re-showing the name on nearly
    //     every row; (b) word-level blocks alternate with punctuation/whitespace
    //     fragments carrying a NULL speaker_label (no name) — those are
    //     TRANSPARENT to the run (neither open nor break a section), so one
    //     speaker's name shows once, not after every punctuation block. Speaker
    //     is read off the anchor (first kept) element, so a deleted lead doesn't
    //     drop the section's provenance. (Per Dmitry, 2026-06-16/17: name shows
    //     only on a real speaker change.)
    //
    // Indexed by block id (both columns iterate the same `blocks`, so a Map
    // keyed by id feeds the identical header + caret anchor to each).
    const blockHeaders = useMemo(() => {
      const byId = new Map<string, BlockHeaderInfo>();
      // Pass 1: resolve each block's start time, caret anchor, speaker label and
      // RESOLVED display name (or `undefined` for a null-speaker block).
      const resolved = blocks.map(block => {
        const { startTime, anchorIndex } = deriveBlockTimeAnchor(
          block,
          elementByInitialIndex,
        );
        const anchorEl =
          elementByInitialIndex.get(anchorIndex) ??
          elementByInitialIndex.get(block.lead);
        // Resolve the block's speaker from its DOMINANT kept speaker — tally
        // `speaker_label` across the block's kept text words and take the most
        // frequent — rather than the single first-timed anchor word. A boundary-
        // spanning segment union can pull in a neighbour's word; majority keeps
        // the block on its real speaker. Falls back to the anchor when no kept
        // word carries a label (deleted-lead robustness preserved).
        const labelCounts = new Map<string, number>();
        // Does the block carry any real speech — a kept element whose value has a
        // letter or digit? A block that is only punctuation / whitespace (a lone
        // "." that diarization mis-assigned to a different speaker, say) holds no
        // real speaker; letting it open a section re-emits a redundant header for
        // the surrounding speaker on the very next block. Tracked alongside the
        // dominant-label tally in one pass over the kept words.
        let hasSpeechContent = false;
        for (const idx of block.originalIndices) {
          const el = elementByInitialIndex.get(idx);
          if (!el || el.state === 'removed' || el.state === 'cut') continue;
          if (!hasSpeechContent && /[\p{L}\p{N}]/u.test(el.value ?? '')) {
            hasSpeechContent = true;
          }
          if (!el.speaker_label) continue;
          labelCounts.set(
            el.speaker_label,
            (labelCounts.get(el.speaker_label) ?? 0) + 1,
          );
        }
        let speakerLabel: string | undefined = anchorEl?.speaker_label;
        let bestCount = 0;
        for (const [label, n] of labelCounts) {
          if (n > bestCount) {
            bestCount = n;
            speakerLabel = label;
          }
        }
        // A block with no real speech holds no speaker: punctuation/whitespace
        // only (above), or a bracketed non-speech marker (e.g. "[Foreign]") —
        // both of which diarization commonly mis-labels. Treat as transparent (no
        // name) so the surrounding speaker run holds and no redundant header
        // re-opens.
        const anchorValue = (anchorEl?.value ?? '').trim();
        const isNonSpeechMarker = /^\[.*\]$/.test(anchorValue);
        const speakerName =
          speakerLabel && hasSpeechContent && !isNonSpeechMarker
            ? getSpeakerName(speakerLabel)
            : undefined;
        return {
          block,
          startTime,
          anchorIndex,
          speakerLabel,
          speakerName,
        };
      });
      // Pass 2: section breaks. New section only when a block that HAS a speaker
      // differs from the last block that had one. Null-speaker blocks
      // (punctuation / whitespace) are transparent: they neither open nor break
      // the run (`markSpeakerSections`).
      const isNewSpeakerFlags = markSpeakerSections(
        resolved.map(r => r.speakerName),
      );
      resolved.forEach((r, i) => {
        byId.set(r.block.id, {
          speakerLabel: r.speakerLabel,
          startTime: r.startTime,
          isNewSpeaker: isNewSpeakerFlags[i],
          caretAnchorIndex: r.anchorIndex,
        });
      });
      return byId;
    }, [blocks, elementByInitialIndex, getSpeakerName]);

    // The active block = the one that CARRIES the active phrase (lead ===
    // currentLead), found by MEMBERSHIP — mirroring the translation column's
    // `phrase.lead === currentLead` selection so both columns always agree
    // (PLAT-10464).
    //
    // The previous lead-order walk ("last block whose `lead <= currentLead`")
    // assumed blocks are ascending by source lead. In a reordered montage
    // `blocks` are in PLAY order, so that walk picked the wrong
    // (earlier-in-source) paragraph and a reordered paragraph was never the
    // active row. `translatedPhrases` is populated for all view modes and holds
    // the phrase whose lead is `currentLead`, so this resolves regardless of
    // block order. Fallbacks keep the old robustness for whole-story / unusual
    // data (incl. a `currentLead` that is a cut/removed lead inside a paragraph,
    // so it isn't an exact `originalIndices` member).
    const sourceActiveBlockId = useMemo(() => {
      if (currentLead === null) return null;
      const byPhrase = blocks.find(b =>
        b.translatedPhrases?.some(p => p.lead === currentLead),
      );
      if (byPhrase) return byPhrase.id;
      const byIndex = blocks.find(b => b.originalIndices.includes(currentLead));
      if (byIndex) return byIndex.id;
      let activeId: string | null = null;
      for (const b of blocks) {
        if (b.lead <= currentLead) activeId = b.id;
        else break;
      }
      return activeId;
    }, [blocks, currentLead]);
    const activeBlockId = dubActive
      ? dubActive.activeBlockId
      : sourceActiveBlockId;
    const isBlockActive = (blockId: string): boolean =>
      dubActive
        ? dubActive.playingBlockIds.has(blockId)
        : blockId === activeBlockId;

    // Two scroll columns; mirror drag-scroll between them (net-new, scoped).
    const leftColRef = useRef<HTMLDivElement>(null);
    const rightColRef = useRef<HTMLDivElement>(null);

    // Word-level paint for the partial-coverage hover indices — same machinery
    // as the linked-selection highlight, under the card-specific names so the
    // two never collide. An empty set clears the name, so hover-off cleans up
    // via the effect body; the cleanup covers unmount.
    useEffect(() => {
      const left = leftColRef.current;
      const right = rightColRef.current;
      const partial = changeHoverCoverage.partialIndices;
      if (left) {
        // PLAT-10611 — `applyLinkedHighlight` takes a `LinkedMirror`, not a raw
        // index Set. The hovered source indices are integer keys, so wrap them
        // as an int-only mirror (no stable-id half).
        applyLinkedHighlight(
          left,
          { mirrorIds: new Set<string>(), mirrorInts: partial },
          CHANGED_CARD_ORIGINAL_HIGHLIGHT,
        );
      }
      if (right) {
        const activePhrases =
          translation?.phrases.filter(p => p.active !== false) ?? [];
        applyLinkedHighlight(
          right,
          resolveLinkedMirror(
            new Set<string>(),
            partial,
            'original',
            activePhrases,
          ),
          CHANGED_CARD_TRANSLATED_HIGHLIGHT,
        );
      }
      return () => {
        if (left) {
          applyLinkedHighlight(
            left,
            EMPTY_LINKED_MIRROR,
            CHANGED_CARD_ORIGINAL_HIGHLIGHT,
          );
        }
        if (right) {
          applyLinkedHighlight(
            right,
            EMPTY_LINKED_MIRROR,
            CHANGED_CARD_TRANSLATED_HIGHLIGHT,
          );
        }
      };
    }, [changeHoverCoverage, translation]);
    // True for the duration of the programmatic active-row scrolls below (the
    // playback follow + the open-shot anchor). `useScrollSync` skips mirroring
    // while it's set, so the two per-column `scrollTo` writes — each moves a
    // column to ITS OWN active row at the 15% anchor, which is a DIFFERENT
    // absolute scrollTop per column because the two languages have different
    // content heights — don't get proportionally re-aligned into each other and
    // fight. Same guard-the-echo idea as the hook's internal `syncing` flag,
    // owned here for our own scroll. `runProgrammaticColumnScroll` keeps it true
    // across the whole (smooth) scroll, releasing one frame after both columns
    // stop moving (see below). Scoped to these two refs only (CLAUDE.md #12).
    const autoScrollingRef = useRef(false);
    // `!hideOriginal`: with the original column unmounted there is no second
    // pane to mirror to. Passing it as `enabled` (rather than leaving `true`
    // and relying on the hook's null-ref bail) is what makes the effect
    // RE-BIND when the column returns — the refs are stable, so `enabled`
    // flipping is the only signal that the node behind `leftColRef` changed.
    useScrollSync(leftColRef, rightColRef, !hideOriginal, autoScrollingRef);

    // Run a programmatic scroll of BOTH columns with mirroring suppressed for
    // its full duration. `scrollFn` does the per-column writes; we hold
    // `autoScrollingRef` true and only release it once BOTH columns have stopped
    // moving for one frame. A single rAF would be wrong for `behavior: 'smooth'`
    // — the browser dispatches the echoed `scroll` events across many frames
    // AFTER the call, so releasing after one frame would leave most of the
    // smooth-scroll echo unsuppressed and the columns would ping-pong. Polling
    // each column's `scrollTop` for stability covers both the smooth follow and
    // the instant open-shot (which stabilises on the very next frame). Returns a
    // cancel fn so the effect can tear the loop down + reset the flag on cleanup.
    const runProgrammaticColumnScroll = useCallback(
      (scrollFn: () => void): (() => void) => {
        autoScrollingRef.current = true;
        scrollFn();
        const cols = [leftColRef.current, rightColRef.current];
        let prev = cols.map(c => c?.scrollTop ?? 0);
        let rafId: number | null = null;
        const settle = () => {
          const now = cols.map(c => c?.scrollTop ?? 0);
          const stable = now.every((v, i) => Math.abs(v - prev[i]) < 0.5);
          if (stable) {
            autoScrollingRef.current = false;
            rafId = null;
            return;
          }
          prev = now;
          rafId = requestAnimationFrame(settle);
        };
        rafId = requestAnimationFrame(settle);
        return () => {
          if (rafId !== null) cancelAnimationFrame(rafId);
          autoScrollingRef.current = false;
        };
      },
      [],
    );

    // Each column feeds BOTH the internal scroll-sync ref above AND the
    // workspace's per-pane linked-highlight ref (PLAT-10287, extended here).
    // Merge them so the single `Column` node lands in both.
    const setLeftCol = useMergedRef(leftColRef, originalColumnRef);
    const setRightCol = useMergedRef(rightColRef, translatedColumnRef);

    // Row-for-row height match (the aligned-view promise). The two columns are
    // independent flows, so when one language wraps to more lines than the other
    // its row is taller and everything below DRIFTS. After layout, pair each
    // block's left + right row (by `data-align-key` = block id) and set both to
    // the taller one's height — corresponding rows then start at the same Y and
    // the columns' total heights match (the scroll-sync then keeps them locked).
    // The per-block header rows (speaker/timestamp) are uniform single lines, so
    // matching the content rows is enough. `useLayoutEffect` applies it before
    // paint (no flash, CLAUDE.md #13); reads all natural heights before writing
    // to avoid layout thrash. Re-runs on `blocks` change and on a column WIDTH
    // change (text re-wraps) — width only, since min-height writes don't change
    // width, so the observer can't loop.
    useLayoutEffect(() => {
      const left = leftColRef.current;
      const right = rightColRef.current;
      if (!right) return;
      // Single column (expanded video): the rows still carry the `minHeight`
      // this pass wrote while there WAS a partner column, so they would stay
      // padded out to the original side's heights and leave gaps under the
      // short ones. Clear them and let the surviving rows size to content.
      if (!left) {
        right
          .querySelectorAll<HTMLElement>('[data-align-key]')
          .forEach(r => (r.style.minHeight = ''));
        return;
      }
      const matchHeights = () => {
        const rightByKey = new Map<string, HTMLElement>();
        right.querySelectorAll<HTMLElement>('[data-align-key]').forEach(r => {
          if (r.dataset.alignKey) rightByKey.set(r.dataset.alignKey, r);
        });
        const pairs: [HTMLElement, HTMLElement][] = [];
        left.querySelectorAll<HTMLElement>('[data-align-key]').forEach(l => {
          const r = l.dataset.alignKey
            ? rightByKey.get(l.dataset.alignKey)
            : undefined;
          if (r) pairs.push([l, r]);
        });
        // Reset so we measure NATURAL content heights, then read all, then write
        // — never interleave reads and writes (avoids layout thrash).
        for (const [l, r] of pairs) {
          l.style.minHeight = '';
          r.style.minHeight = '';
        }
        const maxes = pairs.map(([l, r]) =>
          Math.max(l.offsetHeight, r.offsetHeight),
        );
        pairs.forEach(([l, r], i) => {
          const h = `${maxes[i]}px`;
          l.style.minHeight = h;
          r.style.minHeight = h;
        });
      };
      matchHeights();
      if (typeof ResizeObserver === 'undefined') return;
      let lastWidth = left.clientWidth;
      const ro = new ResizeObserver(() => {
        if (left.clientWidth !== lastWidth) {
          lastWidth = left.clientWidth;
          matchHeights();
        }
      });
      ro.observe(left);
      ro.observe(right);
      return () => ro.disconnect();
      // `coverageAlignSignal`: a badge/spinner mounting changes row HEIGHT only,
      // which neither `blocks` nor the width-gated observer sees — without it the
      // columns drift when a verdict lands.
      //
      // `hideOriginal`: while expanded the pass bails on the missing column and
      // the surviving rows keep whatever `minHeight` the last two-column pass
      // wrote — so they stay padded to the ORIGINAL side's heights, leaving gaps
      // in a column that no longer has a partner. Re-running on the flag clears
      // them going in (single column, natural heights) and re-matches coming
      // back out.
    }, [blocks, coverageAlignSignal, hideOriginal]);

    // Autoscroll to the active row during playback — but ONLY when the row has
    // left the comfortable visible band (PLAT-10264 readability fix). Keyed on
    // `[activeBlockId, isPlaying]`: when playback is running and the active
    // block changes, each column checks whether its active row is still within
    // the band (top anchor → bottom edge, see `isRowOutsideBand`); it scrolls
    // (to `computePlayheadScrollTop`, the same `PLAYHEAD_TOP_OFFSET_RATIO`
    // anchor every transcript view uses) ONLY if the row is outside it. With
    // word-level blocks the active block changes every word; scrolling on every
    // change scrolled on every word ("moves so fast it's barely readable"). The
    // band keeps the view still while the active row is visible and nudges it
    // only at the edges (the prose pane's "scroll when off-screen" feel). Only
    // scroll while playing so a manual scroll while paused isn't yanked back.
    // DOM scoping (CLAUDE.md #12): the active row is found via `[data-active-row]`
    // WITHIN each column ref — never a `document`-global query — so a second
    // transcript instance behind the modal is untouched.
    useEffect(() => {
      if (!isPlaying || followLead === null) return;
      // PLAT-11650 — never scroll a phrase being edited in place away from
      // under its (fixed) bubble.
      if (inlineEdit) return;
      // Keyed on `currentLead` (the active PHRASE), not `activeBlockId` (the
      // block): a Paragraph block spans many phrases, so following the block
      // would never re-scroll as the playhead moves through a long paragraph.
      // The scroll TARGET is the active phrase ELEMENT
      // (`[data-initial-index="${currentLead}"]`), not the row — a paragraph row
      // is tall, so anchoring the row top would push the active phrase
      // off-screen. Falls back to the active row when the phrase element isn't
      // found (Segment/Fragment behave the same: phrase ≈ row). The band guard
      // still keeps it calm (scroll only when the phrase leaves the band).
      // Decide per column BEFORE the suppressed window, then write inside
      // `runProgrammaticColumnScroll` so mirroring stays off for the smooth scroll.
      const writes: (() => void)[] = [];
      for (const col of [leftColRef.current, rightColRef.current]) {
        if (!col) continue;
        // PLAT-10611 (F5, R8) — resolve the active phrase span by its stable lead
        // UUID FIRST (drift-correct), fall back to the integer `currentLead`.
        const target =
          (followLeadUuid
            ? col.querySelector(`[data-source-uuid="${followLeadUuid}"]`)
            : null) ??
          col.querySelector(`[data-initial-index="${followLead}"]`) ??
          col.querySelector('[data-active-row]');
        if (!target) continue;
        if (
          !isRowOutsideBand(
            col.getBoundingClientRect(),
            target.getBoundingClientRect(),
          )
        ) {
          continue;
        }
        const top = computePlayheadScrollTop(col, target);
        writes.push(() => col.scrollTo({ top, behavior: 'smooth' }));
      }
      if (writes.length === 0) return;
      return runProgrammaticColumnScroll(() => writes.forEach(w => w()));
    }, [
      followLead,
      followLeadUuid,
      isPlaying,
      inlineEdit,
      runProgrammaticColumnScroll,
    ]);

    // One-shot open anchor (PLAT-10264): the review opens PAUSED mid-document,
    // so the off-screen-only playback scroll above never fires on open and the
    // active row can sit below the fold. Once, after the first render that has
    // an active block, bring that row into view (instant, not smooth — it's the
    // initial position, not a follow). Guarded by a ref so it runs a single
    // time per mount; subsequent active-row motion is owned by the playback
    // effect. Scoped to the column refs (CLAUDE.md #12).
    const didOpenScrollRef = useRef(false);
    useEffect(() => {
      if (didOpenScrollRef.current || activeBlockId === null) return;
      const writes: (() => void)[] = [];
      for (const col of [leftColRef.current, rightColRef.current]) {
        if (!col) continue;
        // Anchor the active PHRASE element (not the tall paragraph row) so the
        // review opens with the paused phrase in view, mirroring the playback
        // follow. Falls back to the active row when the phrase isn't resolvable.
        // PLAT-10611 (F5, R8) — id-first (stable lead UUID), integer fallback.
        const target =
          (followLeadUuid
            ? col.querySelector(`[data-source-uuid="${followLeadUuid}"]`)
            : null) ??
          (followLead !== null
            ? col.querySelector(`[data-initial-index="${followLead}"]`)
            : null) ??
          col.querySelector('[data-active-row]');
        if (!target) continue;
        const top = computePlayheadScrollTop(col, target);
        writes.push(() => col.scrollTo({ top }));
      }
      // Only latch once we actually found+scrolled a target, so a first render
      // before the active row is in the DOM doesn't burn the one shot. Run the
      // (instant) writes with mirroring suppressed too — same per-column 15%
      // anchor that differs between the two columns' heights.
      if (writes.length === 0) return;
      didOpenScrollRef.current = true;
      return runProgrammaticColumnScroll(() => writes.forEach(w => w()));
    }, [
      activeBlockId,
      followLead,
      followLeadUuid,
      runProgrammaticColumnScroll,
    ]);

    // Changes-card click reveal (workspace `revealRequest`): bring the change's
    // source word into view in BOTH columns. The playback follow above is
    // playing-only by design, so a paused card click needs this explicit
    // scroll. Nonce-guarded so each click fires once (repeat clicks bump the
    // nonce); the band guard skips columns where the word is already visible.
    const lastRevealNonceRef = useRef<number | null>(null);
    useEffect(() => {
      if (!revealRequest || revealRequest.nonce === lastRevealNonceRef.current)
        return;
      lastRevealNonceRef.current = revealRequest.nonce;
      const writes: (() => void)[] = [];
      for (const col of [leftColRef.current, rightColRef.current]) {
        if (!col) continue;
        const target =
          col.querySelector(
            `[data-initial-index="${revealRequest.initialIndex}"]`,
          ) ?? col.querySelector('[data-active-row]');
        if (!target) continue;
        if (
          !isRowOutsideBand(
            col.getBoundingClientRect(),
            target.getBoundingClientRect(),
          )
        ) {
          continue;
        }
        const top = computePlayheadScrollTop(col, target);
        writes.push(() => col.scrollTo({ top, behavior: 'smooth' }));
      }
      if (writes.length === 0) return;
      return runProgrammaticColumnScroll(() => writes.forEach(w => w()));
    }, [revealRequest, runProgrammaticColumnScroll]);

    if (!translation) {
      return <Empty>No translation data available for {language}.</Empty>;
    }
    if (blocks.length === 0) {
      return <Empty>No content to align.</Empty>;
    }

    return (
      <Grid $single={hideOriginal}>
        {!hideOriginal && (
          <Column
            ref={setLeftCol}
            aria-label="Original"
            data-aligned-column="original"
          >
            {blocks.map(block => {
              const isActive = isBlockActive(block.id);
              const header = blockHeaders.get(block.id);
              // The header row and the block `<Row>` are FLAT SIBLINGS inside the
              // Column (the Fragment adds no DOM node), so cross-row text selection
              // in the original column is preserved (CLAUDE.md "Text Selection and
              // DOM Structure").
              return (
                <React.Fragment key={`o-${block.id}`}>
                  {header &&
                    renderHeaderRow(
                      block,
                      header,
                      'o-',
                      resolveSpeakerName,
                      resolveSpeakerColor,
                    )}
                  <Row
                    // PLAT-11650 follow-up — the source column keeps only the
                    // playhead's gold bar for "you are here"; the row wash
                    // stays on the translated column, where the work is.
                    $active={false}
                    $changeHover={changeHoverCoverage.fullyCoveredBlockIds.has(
                      block.id,
                    )}
                    data-change-hover={
                      changeHoverCoverage.fullyCoveredBlockIds.has(block.id)
                        ? ''
                        : undefined
                    }
                    data-active-row={isActive ? '' : undefined}
                    data-align-key={block.id}
                  >
                    {proxyPhrasesByBlock
                      ? // Proxy reference mode (PLAT-10655): the reference
                        // language's phrases, read-only — click seeks, the
                        // active phrase tracks the reviewed `currentLead` by
                        // source-index containment. No redline / caret / edit.
                        (proxyPhrasesByBlock.get(block.id) ?? []).map(
                          (unit, pi, units) => (
                            <React.Fragment
                              key={`ref-${block.id}-${unit.lead}-${pi}`}
                            >
                              {pi > 0 &&
                              needsSpaceBetweenPhrases(
                                units[pi - 1]?.value ?? '',
                                unit.value,
                              )
                                ? ' '
                                : null}
                              <Word
                                data-initial-index={unit.lead}
                                className={
                                  [
                                    // PLAT-11765 — the reference column follows
                                    // the same playhead as the two it sits
                                    // between: bolder while its segment plays,
                                    // brighter once the playhead has passed it.
                                    isActive ? 'playing' : '',
                                    currentTime === undefined
                                      ? ''
                                      : (dubPosition?.phraseRead(unit.phrase) ??
                                          isPhraseRead(
                                            unit.phrase,
                                            currentTime,
                                          ))
                                        ? 'read'
                                        : 'unread',
                                  ]
                                    .filter(Boolean)
                                    .join(' ') || undefined
                                }
                                onClick={() => seek(unit.lead)}
                              >
                                {unit.value}
                              </Word>
                            </React.Fragment>
                          ),
                        )
                      : renderOriginalWords(
                          block,
                          sourceElements ?? [],
                          posByInitialIndex,
                          isActive,
                          isPlaying,
                          seek,
                          clipScoped,
                          posBySourceUuid,
                          currentTime,
                          dubPosition
                            ? el => dubPosition.sourceRead(el)
                            : undefined,
                        )}
                  </Row>
                </React.Fragment>
              );
            })}
          </Column>
        )}
        <Column
          ref={setRightCol}
          aria-label="Translated"
          data-aligned-column="translated"
        >
          {blocks.map(block => {
            const isActive = isBlockActive(block.id);
            const header = blockHeaders.get(block.id);
            // Resolved once for both columns (see `coverageIndicatorsByBlock`)
            // so the original column can emit a height-matching partner row.
            const coverageIndicators = coverageIndicatorsByBlock.get(block.id);
            // BOTH columns emit the identical header sequence (same blocks /
            // speakers), so the two columns stay row-aligned. Translated-column
            // redline is OUT OF SCOPE: a block renders one `<Word>` of active
            // translation and carries no per-element removed/cut data to strike.
            // PLAT-11340 follow-up — which of this block's phrases changed ONLY
            // by losing words. Those get no green: the tint marks "these words
            // are new", and after a deletion none of the words on screen are.
            // Resolved once per block and reused inside the phrase loop below
            // rather than recomputed per phrase (the predicate runs a diff).
            const deletionOnlyLeads = new Set<number>();
            // The struck baseline a REMOVED phrase renders in place of its
            // (empty) value. Per block, because the space between two phrases
            // is decided against the PREVIOUS one too, and
            // `needsSpaceBetweenPhrases` is false whenever either side trims
            // empty — deciding on a removed phrase's `''` dropped the space on
            // both sides and glued the ghost to its neighbours.
            const ghostTextByLead = new Map<number, string>();
            for (const tp of block.translatedPhrases ?? []) {
              if (isDeletionOnlyChange(tp.phrase, translation?.phrases)) {
                deletionOnlyLeads.add(tp.lead);
              }
              if (isTranslationRemoved(tp.phrase, translation?.phrases)) {
                // `resolveOriginalValue`, NOT `resolveChangedPhrase`'s
                // `previousValue`: for an override fragment the latter is the
                // whole segment's original (it pairs with an Undo that reverts
                // the whole override), so a split override's removed word
                // struck out the entire sentence. This is the same
                // fragment-scoped baseline `isTranslationRemoved` just used to
                // call it removed.
                const ghost = resolveOriginalValue(
                  tp.phrase,
                  translation?.phrases,
                );
                if (ghost) ghostTextByLead.set(tp.lead, ghost);
              }
            }
            /** What a phrase actually RENDERS — its ghost when removed. */
            const renderedPhraseText = (unit?: {
              lead: number;
              value: string;
            }): string =>
              unit ? (ghostTextByLead.get(unit.lead) ?? unit.value) : '';
            return (
              <React.Fragment key={`t-${block.id}`}>
                {header &&
                  renderHeaderRow(
                    block,
                    header,
                    't-',
                    resolveSpeakerName,
                    resolveSpeakerColor,
                  )}
                <Row
                  $deletion={deletionOnlyLeads.size > 0}
                  $active={isActive}
                  $changeHover={changeHoverCoverage.fullyCoveredBlockIds.has(
                    block.id,
                  )}
                  data-change-hover={
                    changeHoverCoverage.fullyCoveredBlockIds.has(block.id)
                      ? ''
                      : undefined
                  }
                  data-active-row={isActive ? '' : undefined}
                  data-align-key={block.id}
                >
                  {block.translatedPhrases &&
                  block.translatedPhrases.length > 0 ? (
                    // Paragraph blocks: render each phrase as its own click-to-
                    // seek target (PLAT-10288 follow-up) so the translated column
                    // seeks INTO the paragraph, not just to its start. The active
                    // phrase (lead === currentLead) gets the playhead style.
                    block.translatedPhrases.map((phrase, pi) => {
                      // Changed-phrase mark (PLAT-10265), resolved by the shared
                      // kit so the modal and the prose surface share one source
                      // of truth. `isEdited` lights single-phrase edits and
                      // span-replace override fragments alike, and routes a click
                      // into the editor popover (whose "Revert to original" is the
                      // Undo) instead of seeking.
                      const ph = phrase.phrase;
                      // PLAT-10266 (Card 6) — the CANONICAL stale key is
                      // `min(originalIndices)`, which is what `staleLeads` and the
                      // resolve handlers use. In Segment/Fragment view that equals
                      // `phrase.lead`, but in PARAGRAPH view `phrase.lead` is the
                      // first-RENDERED member's index (render-position lead), which
                      // diverges when the covered lead word was itself edited — so
                      // keying the amber + resolve menu on `phrase.lead` there
                      // missed the flag. Derive the canonical key so a rendered
                      // stale Paragraph fragment lights amber and its resolve menu
                      // targets the right fragment.
                      const staleKey =
                        ph.originalIndices.length > 0
                          ? Math.min(...ph.originalIndices)
                          : phrase.lead;
                      const isStalePhrase = staleLeads?.has(staleKey) ?? false;
                      // PLAT-11650 — is THIS phrase the one being edited in place?
                      const inlineEditingHere =
                        inlineEdit !== null &&
                        inlineEdit.blockId === block.id &&
                        inlineEdit.lead === phrase.lead;
                      const { isEdited, overrideId } = resolveChangedPhrase(
                        ph,
                        translation?.phrases,
                        overrideOriginalById,
                      );
                      // PLAT-11340 follow-up — a REMOVED translation (the
                      // reviewer blanked this phrase; the source words still
                      // play). Stored as an empty correction, so it arrives here
                      // as `isEdited` with nothing to render — an invisible,
                      // un-right-clickable gap. Render the machine baseline
                      // struck through instead, reusing the source column's own
                      // redline grammar, so the reviewer can see what they
                      // removed and get it back.
                      const isRemoved = isTranslationRemoved(
                        ph,
                        translation?.phrases,
                      );
                      // Read from the block pre-pass so the text rendered here
                      // is the same one the spacing above was decided on.
                      const removedGhostText =
                        ghostTextByLead.get(phrase.lead) ?? '';
                      // PLAT-10270 — narrow the green to the changed words. A
                      // "Fix Translation" correction is one fragment over the
                      // whole span, so a per-fragment green paints the whole
                      // segment. Break it into word tokens and green only the
                      // words that differ from the machine baseline. Switch to
                      // token rendering ONLY for a PARTIAL change (some words
                      // unchanged); a single word or a full rewrite keeps the
                      // whole-word mark and its playhead CSS untouched.
                      const wordTokens = isEdited
                        ? resolveChangedWordTokens(ph, translation?.phrases)
                        : null;
                      const partialChange =
                        !!wordTokens &&
                        wordTokens.some(t => t.changed) &&
                        wordTokens.some(
                          t => !t.changed && t.text.trim().length > 0,
                        );
                      // PLAT-11482 — word atoms for a phrase the splitter
                      // could not fragment. Empty for Latin/Cyrillic (and for
                      // any already-atomic phrase), so those render exactly as
                      // before. A partially-changed phrase keeps the
                      // changed-word marks instead: the two are different
                      // per-word renderings of the same text and must not
                      // nest.
                      const wordAtoms = partialChange
                        ? []
                        : buildTranslatedWordAtoms(
                            phrase.value,
                            ph.originalIndices,
                            isStoppableIndex,
                          );
                      // The Changes-panel entry key this phrase belongs to —
                      // same derivation as the panel's ChangeEntry.key.
                      const changeKey = isEdited
                        ? (overrideId ?? `f:${ph.originalIndices.join(',')}`)
                        : undefined;
                      // PLAT-10850 (E13) — the glossary term string(s) to
                      // underline INSIDE this fragment (target term, or source
                      // term for a DNT entry). Empty unless the fragment is
                      // TM-backed AND its entries resolve — then only the term is
                      // marked, not the whole fragment.
                      const tmTerms = isTmBacked(ph)
                        ? (ph.tmEntryIds ?? [])
                            .map(id => tmEntriesById?.get(id)?.translation)
                            .filter((t): t is string => !!t)
                        : [];
                      // PLAT-11906 — while a dub plays, read by the dub: whole,
                      // not yet, or up to a character inside this fragment when
                      // its line reads word by word. Counted on the trimmed
                      // text, so the leading run is added back here.
                      const readByDub = dubPosition?.fragmentRead(
                        reviewFragmentKey(ph),
                      );
                      const readChars =
                        typeof readByDub === 'number' && !inlineEditingHere
                          ? phrase.value.length -
                            phrase.value.trimStart().length +
                            readByDub
                          : undefined;
                      return (
                        <React.Fragment
                          // `phrase.lead` alone isn't unique: malformed data can
                          // carry two active phrases sharing a source lead (a
                          // coverage-invariant violation). The map index keeps
                          // the key unique so React doesn't drop/duplicate rows.
                          key={`tp-${block.id}-${phrase.lead}-${pi}`}
                        >
                          {pi > 0 &&
                          needsSpaceBetweenPhrases(
                            renderedPhraseText(
                              block.translatedPhrases?.[pi - 1],
                            ),
                            renderedPhraseText(phrase),
                          )
                            ? ' '
                            : null}
                          <TranslatedFragment
                            readChars={readChars}
                            data-initial-index={phrase.lead}
                            // PLAT-12024 — the dub line this fragment is filed
                            // under, for the landed pulse to find its words.
                            // Only a real `parentId`: a phrase without one is
                            // never in a re-dub's coverage and must not light.
                            data-segment-id={ph.parentId}
                            // PLAT-10611 (F5, P5) — stamp the lead element's
                            // stable UUID beside the retained integer lead so the
                            // cross-language link resolves this translated word by
                            // identity FIRST; omitted when the lead has no id.
                            data-source-uuid={leadSourceUuid(phrase.lead)}
                            className={
                              [
                                translatedPhraseClassName(
                                  // PLAT-11765 — the helper's playhead output is
                                  // the gold fill or the caret, and the review
                                  // wants neither; it marks the playing segment
                                  // by weight instead (`playing`, below). The
                                  // helper is still what paints edited / stale.
                                  PLAYHEAD_USES_FILL_OR_CARET,
                                  PLAYHEAD_USES_FILL_OR_CARET,
                                  // A deletion-only change is marked by the
                                  // row's score, not by tinting text that did
                                  // not change (PLAT-11340 f/u).
                                  isEdited &&
                                    !partialChange &&
                                    !deletionOnlyLeads.has(phrase.lead),
                                  // PLAT-10266 — amber stale mark: the reviewer
                                  // edited the source words this fragment covers.
                                  // Keyed on the canonical min-index (`staleKey`)
                                  // so it lights in Paragraph view too (Card 6).
                                  isStalePhrase,
                                ),
                                // PLAT-11482 — hand the playhead fill to the
                                // inner active word instead of the whole phrase.
                                wordAtoms.length > 0 ? 'word-scoped' : '',
                                // PLAT-11765 — the playing segment reads
                                // slightly bolder. Segment-level for now; the
                                // playing WORD can take over the emphasis once
                                // the translated side carries word timings.
                                isActive ? 'playing' : '',
                                // PLAT-11765 — reached text reads in the primary
                                // token, text still ahead in the muted one. The
                                // segment playing right now counts as reached,
                                // so it brightens whole when it starts instead
                                // of a fragment at a time as each one finishes.
                                currentTime === undefined
                                  ? ''
                                  : (
                                        readByDub !== undefined
                                          ? readByDub !== false
                                          : isPhraseRead(ph, currentTime)
                                      )
                                    ? 'read'
                                    : 'unread',
                                // PLAT-11650 — the field paints its own box; the
                                // span stands down (no hover tint, no fill).
                                inlineEditingHere ? 'inline-editing' : '',
                              ]
                                .filter(Boolean)
                                .join(' ') || undefined
                            }
                            // PLAT-11650 — a single click SEEKS, as in
                            // Transcript & Speakers, so a selection and the
                            // player's Space shortcut keep working (an open
                            // field is a focused contenteditable, and Space
                            // would type). A DOUBLE-click in Paragraph /
                            // Segment seeks AND opens the phrase for typing in
                            // place, caret at the clicked character (resolved
                            // BEFORE the children swap, while the span still
                            // holds the rendered text). Words view — and a
                            // REMOVED translation, whose struck ghost is the
                            // handle to restore it — keep the older contract on
                            // either gesture: a changed phrase opens the editor
                            // popover (its Revert is the Undo), an unchanged
                            // one seeks.
                            onClick={e => {
                              if (inlineEditingHere) return;
                              // Selection precedence (PLAT-11340 f/u) — the same
                              // rule `onContextMenu` below applies. A drag-select
                              // that ENDS on this phrase fires a click; with a
                              // live selection the span path owns the gesture.
                              if (
                                isPhraseInTranslatedSelection(
                                  translatedColumnRef?.current ?? null,
                                  translation?.phrases,
                                  phrase.phrase,
                                )
                              ) {
                                return;
                              }
                              const action = isRemoved
                                ? isEdited
                                  ? 'popover'
                                  : 'seek'
                                : resolveTranslatedPhraseClickAction({
                                    viewMode,
                                    editable,
                                    isEdited,
                                    value: phrase.value,
                                    gesture: 'click',
                                  });
                              if (action === 'popover') {
                                handleOpenEditor(ph, e.currentTarget);
                                return;
                              }
                              seek(phrase.lead);
                              // A single click on a phrase that a double-click
                              // WOULD open gets the hint, until this browser has
                              // learned the gesture and at most a few times per
                              // load: nothing else on screen names it, and both
                              // usability testers reached for a single click.
                              if (
                                viewMode !== 'fragment' &&
                                editable &&
                                !isRemoved &&
                                phrase.value.trim() !== '' &&
                                !hasLearnedDoubleClickEdit() &&
                                hintShowsThisLoadRef.current < HINT_MAX_PER_LOAD
                              ) {
                                hintShowsThisLoadRef.current += 1;
                                setEditHintAnchor(e.currentTarget);
                              }
                            }}
                            onDoubleClick={e => {
                              if (inlineEditingHere || isRemoved) return;
                              clearPencilTimers();
                              setHoverPencil(null);
                              const action = resolveTranslatedPhraseClickAction(
                                {
                                  viewMode,
                                  editable,
                                  isEdited,
                                  value: phrase.value,
                                  gesture: 'double-click',
                                },
                              );
                              if (action !== 'seek-and-edit') return;
                              // The browser's double-click just selected a
                              // word; that selection is the gesture's own, not
                              // a span the reviewer drew, so it does not take
                              // precedence here — and it must not linger under
                              // the field's caret.
                              window.getSelection()?.removeAllRanges();
                              seek(phrase.lead);
                              learnDoubleClickEdit();
                              openInlineEdit({
                                phrase: ph,
                                blockId: block.id,
                                lead: phrase.lead,
                                anchorElement: e.currentTarget,
                                caretOffset: resolveCaretOffsetAtPoint(
                                  e.currentTarget,
                                  e.clientX,
                                  e.clientY,
                                  phrase.value.length,
                                ),
                              });
                            }}
                            // Hovering a changed phrase lights its card in the
                            // Changes panel (reverse of the card-hover reveal).
                            onMouseEnter={e => {
                              if (changeKey && onChangedPhraseHover) {
                                onChangedPhraseHover(changeKey);
                              }
                              // PLAT-11650 — the pencil, after a beat, on a
                              // phrase a double-click would open.
                              if (
                                viewMode === 'fragment' ||
                                !editable ||
                                isRemoved ||
                                inlineEditingHere ||
                                phrase.value.trim() === ''
                              ) {
                                return;
                              }
                              const anchorElement = e.currentTarget;
                              clearPencilTimers();
                              pencilShowTimerRef.current = setTimeout(() => {
                                pencilShowTimerRef.current = undefined;
                                setHoverPencil({
                                  phrase: ph,
                                  blockId: block.id,
                                  lead: phrase.lead,
                                  anchorElement,
                                });
                              }, 120);
                            }}
                            onMouseLeave={() => {
                              if (changeKey && onChangedPhraseHover) {
                                onChangedPhraseHover(null);
                              }
                              if (pencilShowTimerRef.current) {
                                clearTimeout(pencilShowTimerRef.current);
                                pencilShowTimerRef.current = undefined;
                              }
                              schedulePencilHide();
                            }}
                            // Right-click "Edit translation" — identical guard +
                            // payload to the inline prose view's `renderPhrase`
                            // (no menu on a whitespace-only phrase or when not
                            // editable). Edits the carried `phrase` by its
                            // `originalIndices`.
                            onContextMenu={e => {
                              if (inlineEditingHere) return;
                              // A removed translation is deliberately empty,
                              // so it must NOT be caught by the whitespace bail
                              // — its struck ghost is the only handle the
                              // reviewer has to restore it (PLAT-11340 f/u).
                              if (
                                !editable ||
                                (phrase.value.trim() === '' && !isRemoved)
                              )
                                return;
                              // PLAT-10266 (Card R2) — a STALE fragment that IS the
                              // current single-stale selection routes to the resolve
                              // menu BEFORE the precedence bail below. That ordering is
                              // the whole secondary defect: the bail ran first, so
                              // right-clicking an amber word while a selection covered
                              // it gave "Fix Translation" (reproduced live in all three
                              // views). Gated on `staleSelectionLead === staleKey`, the
                              // decision `useSpanReplace` already made on the WIDENED
                              // set, so a MIXED selection still falls through and the
                              // span path keeps precedence.
                              if (
                                onResolveRetranslate &&
                                isStalePhrase &&
                                staleSelectionLead !== null &&
                                staleSelectionLead !== undefined &&
                                staleSelectionLead === staleKey
                              ) {
                                e.preventDefault();
                                e.stopPropagation();
                                // Right-click OWNS this menu — a later selection
                                // change must not auto-close it (Card R2).
                                resolveMenuFromHighlightRef.current = false;
                                setResolveMenu({
                                  lead: staleKey,
                                  anchorElement: e.currentTarget,
                                });
                                return;
                              }
                              // Selection precedence (PLAT-10467): when a
                              // non-empty translated-column selection already
                              // covers this word, the span path owns the edit —
                              // let the floating "Fix Translation" trigger /
                              // ReplaceSpanEditor win and suppress the
                              // single-word menu (preventDefault + return, no
                              // menu). The span path reads the SAME
                              // translatedColumnRef, so both sides agree on what
                              // counts as the selection.
                              if (
                                isPhraseInTranslatedSelection(
                                  translatedColumnRef?.current ?? null,
                                  translation?.phrases,
                                  phrase.phrase,
                                )
                              ) {
                                e.preventDefault();
                                return;
                              }
                              // PLAT-10266 (Card 2) — a STALE (amber) fragment
                              // opens the inline resolve menu (Re-translate
                              // machine; Card 3 adds Replace manually) instead of
                              // the plain "Fix Translation" pill. Review-scoped:
                              // only when `onResolveRetranslate` is wired (the
                              // whole-story host), and attached at the SAME render
                              // site as the amber class, so it works in every view.
                              if (onResolveRetranslate && isStalePhrase) {
                                e.preventDefault();
                                e.stopPropagation();
                                // Card 6 — the resolve handlers look the fragment
                                // up by `min(originalIndices)`, so anchor the menu
                                // on the canonical `staleKey`, not the
                                // render-position `phrase.lead` (they diverge in
                                // Paragraph view).
                                // Right-click owns this menu (Card R2).
                                resolveMenuFromHighlightRef.current = false;
                                setResolveMenu({
                                  lead: staleKey,
                                  anchorElement: e.currentTarget,
                                });
                                return;
                              }
                              e.preventDefault();
                              e.stopPropagation();
                              handleContextMenuRequest(
                                phrase.phrase,
                                e.currentTarget,
                                {
                                  x: e.clientX,
                                  y: e.clientY,
                                },
                              );
                            }}
                          >
                            {/* Two independent marks compose here, outer and
                                inner. PLAT-10270 greens only the words that
                                actually changed (outer); PLAT-10850 underlines
                                the glossary term inside the fragment (inner).
                                A fragment can be BOTH partially edited and
                                glossary-backed, so each word token's text is
                                run through the term marker rather than picking
                                one mark over the other. A multi-word term that
                                straddles a changed/unchanged boundary is
                                underlined as adjacent pieces — visually
                                continuous, and no provenance is lost. */}
                            {/* A REMOVED translation renders its struck
                                machine baseline (PLAT-11340 f/u). A sibling of
                                the chain below rather than a branch inside it:
                                the phrase's applied value is '', so that chain
                                already falls through to its tail and emits
                                nothing — there is no branch to displace, and
                                nesting one more level would reindent all of
                                it. */}
                            {isRemoved && removedGhostText && (
                              <RemovedTranslationGhost aria-label="Removed translation">
                                {removedGhostText}
                              </RemovedTranslationGhost>
                            )}
                            {inlineEditingHere && inlineEdit ? (
                              <InlineTranslatedPhraseField
                                initialValue={inlineCurrentValue}
                                caretOffset={inlineEdit.caretOffset}
                                anchorElement={inlineEdit.anchorElement}
                                onCommit={draft =>
                                  handleInlineCommit(ph, draft)
                                }
                                onCancel={cancelInlineEdit}
                                canRevert={isEdited}
                                onRevert={() => {
                                  handleRevertAndSync(ph);
                                  cancelInlineEdit();
                                }}
                              />
                            ) : partialChange && wordTokens ? (
                              wordTokens.map((t, ti) =>
                                t.changed ? (
                                  <ChangedWordMark key={`w-${ti}`}>
                                    {renderTmMarkedText(t.text, tmTerms)}
                                  </ChangedWordMark>
                                ) : (
                                  <React.Fragment key={`w-${ti}`}>
                                    {renderTmMarkedText(t.text, tmTerms)}
                                  </React.Fragment>
                                ),
                              )
                            ) : wordAtoms.length > 0 ? (
                              /* PLAT-11482 — real word units for a phrase the
                                 splitter could not fragment. Separators are
                                 emitted verbatim between atoms (Korean is
                                 full-width AND space-delimited), so the
                                 rendered text still reproduces `phrase.value`
                                 exactly — including any leading/trailing run,
                                 which the tiler drops but the text must not.
                                 Clicking a word seeks to ITS own source word
                                 rather than the phrase start; an edited phrase
                                 lets the click bubble so the editor popover
                                 still wins. */
                              wordAtoms.map((atom, ai) => (
                                <React.Fragment key={`a-${ai}`}>
                                  {phrase.value.slice(
                                    ai === 0 ? 0 : wordAtoms[ai - 1].endOffset,
                                    atom.startOffset,
                                  )}
                                  <TranslatedWordAtomSpan
                                    onClick={e => {
                                      if (isEdited) return;
                                      e.stopPropagation();
                                      seek(atom.sourceIndex);
                                    }}
                                  >
                                    {renderTmMarkedText(atom.text, tmTerms)}
                                  </TranslatedWordAtomSpan>
                                  {ai === wordAtoms.length - 1
                                    ? phrase.value.slice(atom.endOffset)
                                    : null}
                                </React.Fragment>
                              ))
                            ) : (
                              renderTextWithMarkers(
                                phrase.value,
                                tmTerms,
                                markersByFragment.get(reviewFragmentKey(ph)),
                                dubMarkers,
                                {
                                  busy:
                                    dubStateBySegment?.get(
                                      ph.parentId ??
                                        ph.originalElementIds?.[0] ??
                                        '',
                                    )?.status === 'updating',
                                  onRemove: onMarkerRemove,
                                  onEdit: onMarkerEdit,
                                  editedLines: editedMarkerLines,
                                },
                              )
                            )}
                          </TranslatedFragment>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    // Unsplit-block fallback (no `translatedPhrases`). NO TM term
                    // underline here by design (PLAT-10850): the mark is
                    // honored-only per-phrase provenance (`phrase.tmEntryIds`),
                    // which this block-level path doesn't carry — matching
                    // glossary terms against the raw block text would be an
                    // un-provenanced guess and could false-underline.
                    <Word
                      data-initial-index={block.lead}
                      // PLAT-10611 (F5, P6) — unsplit block fallback: stamp the
                      // lead element's stable UUID beside the retained integer.
                      data-source-uuid={leadSourceUuid(block.lead)}
                      // PLAT-11765 — bolder while it plays; no fill, no caret
                      // (see `renderOriginalWords`).
                      className={isActive ? 'playing' : undefined}
                      onClick={() => seek(block.lead)}
                    >
                      {block.translatedValue}
                    </Word>
                  )}
                  {coverageIndicators ? (
                    // The score sits in the BOTTOM-RIGHT CORNER OF THE GREY AREA
                    // — inside `Row` (which paints the tint), after the words, and
                    // ALWAYS on its own line (Denys, 2026-07-14). `CoverageRow` is
                    // a full-width block, so it breaks the inline word flow onto a
                    // new line by construction rather than by a fragile margin.
                    // Being inside `Row` again means the existing height-match
                    // pass keeps the columns aligned via the block's own key — no
                    // partner row needed in the original column.
                    <CoverageRow>
                      {coverageIndicators.map(indicator => (
                        <CoverageSlot
                          key={`${indicator.leadUuid}-${indicator.kind}`}
                          role={
                            indicator.kind === 'loading' ? 'status' : undefined
                          }
                          aria-label={
                            indicator.kind === 'loading'
                              ? 'Checking coverage…'
                              : undefined
                          }
                        >
                          {indicator.kind === 'loading' ? (
                            <CoverageSpinner size="sm" />
                          ) : indicator.verdict ? (
                            <CoverageBadge
                              verdict={indicator.verdict}
                              onClick={
                                onCoverageBadgeClick
                                  ? anchorEl =>
                                      handleBadgeClick(
                                        indicator.leadUuid,
                                        anchorEl,
                                      )
                                  : undefined
                              }
                              onHoverOpen={
                                onCoverageBadgeClick
                                  ? anchorEl =>
                                      handleBadgeHoverOpen(
                                        indicator.leadUuid,
                                        anchorEl,
                                      )
                                  : undefined
                              }
                              onHoverClose={
                                onCoverageBadgeClick
                                  ? handleBadgeHoverClose
                                  : undefined
                              }
                            />
                          ) : null}
                        </CoverageSlot>
                      ))}
                    </CoverageRow>
                  ) : null}
                  {dubStatusesByBlock
                    .get(block.id)
                    ?.map(({ segmentId, state }) => (
                      <DubStatusRow
                        key={`dub-${segmentId}`}
                        role="status"
                        data-testid="dub-status"
                        data-dub-status={state.status}
                        $failed={state.status === 'failed'}
                      >
                        {state.status === 'updating' ? (
                          <>
                            <CoverageSpinner size="sm" />
                            Updating dubbing…
                          </>
                        ) : state.status === 'updated' ? (
                          state.note ? (
                            `Dub updated · ${state.note}`
                          ) : (
                            'Dub updated'
                          )
                        ) : (
                          <>
                            <span title={state.error}>Dub out of date</span>
                            {onRetryDub && (
                              <>
                                <DubStatusDot aria-hidden="true">
                                  ·
                                </DubStatusDot>
                                <InlineTextAction
                                  type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    onRetryDub(segmentId);
                                  }}
                                >
                                  Retry
                                </InlineTextAction>
                              </>
                            )}
                          </>
                        )}
                      </DubStatusRow>
                    ))}
                </Row>
              </React.Fragment>
            );
          })}
        </Column>
        {/* Right-click menu + edit popover — both `position: fixed`
            (z-index 100001), so mounting them inside the Grid doesn't clip them.
            Wired to the SAME `usePhraseEditing` handlers + `resolve*` helpers as
            the inline prose views, so the edit/revert flow is identical. */}
        {editHintAnchor && (
          <InlineEditHint
            anchorElement={editHintAnchor}
            onDismiss={() => setEditHintAnchor(null)}
          />
        )}
        {hoverPencil && !inlineEdit && (
          <PhraseEditPencil
            anchorElement={hoverPencil.anchorElement}
            onHoverChange={handlePencilHoverChange}
            onEdit={() => {
              const target = hoverPencil;
              clearPencilTimers();
              setHoverPencil(null);
              learnDoubleClickEdit();
              // PLAT-11725 — seek first, exactly as the double-click does:
              // the playhead is what lights the row, so without it the field
              // opens on a phrase whose row is still unlit while a DIFFERENT
              // row carries the active background — the edit doesn't read as
              // being here.
              seek(target.lead);
              openInlineEdit({
                phrase: target.phrase,
                blockId: target.blockId,
                lead: target.lead,
                anchorElement: target.anchorElement,
                caretOffset: target.phrase.translatedValue.length,
              });
            }}
          />
        )}
        {contextMenu && (
          <TranslatedPhraseContextMenu
            positionXY={contextMenu.positionXY}
            onEdit={handleMenuEdit}
            onClose={handleMenuClose}
            // PLAT-11340 follow-up — Remove / Restore, exactly one of the two,
            // chosen by the target phrase's current state. Wired here only: the
            // menu's three other hosts (inline prose, redline diff, captions)
            // pass neither and render the single-action menu they always did.
            {...(isTranslationRemoved(contextMenu.phrase, translation?.phrases)
              ? {
                  onRestore: () => {
                    handleRevertAndSync(contextMenu.phrase);
                    handleMenuClose();
                  },
                }
              : {
                  onRemove: () => {
                    handleSave(contextMenu.phrase, '');
                    handleMenuClose();
                  },
                })}
          />
        )}
        {/* PLAT-10266 (Card 2) — the inline resolve menu for a stale (amber)
            fragment. `position: fixed` (z 100001, like the menus above), anchored
            to the right-clicked fragment word. Only mounts when the review wired
            `onResolveRetranslate` (whole-story host). */}
        {resolveMenu && onResolveRetranslate && (
          <StaleFragmentResolveMenu
            anchorElement={resolveMenu.anchorElement}
            onReTranslate={() => onResolveRetranslate(resolveMenu.lead)}
            // PLAT-10266 (Card 3) — the "Replace manually" sibling row, wired
            // only when the review passes `onResolveManual` (same whole-story
            // gate as re-translate); omitted otherwise so the row never appears.
            onReplaceManually={
              onResolveManual
                ? () => onResolveManual(resolveMenu.lead)
                : undefined
            }
            onClose={() => {
              resolveMenuFromHighlightRef.current = false;
              setResolveMenu(null);
            }}
          />
        )}
        {editing && (
          <TranslatedPhraseEditorPopover
            anchorElement={editing.anchorElement}
            // Resolved once: it walks every base element, and both the
            // read-only source row and the TM promote seed need it.
            sourceText={editingSourceText}
            sourceLanguageLabel={
              translation?.sourceLanguage
                ? formatLanguageCaption(translation.sourceLanguage)
                : undefined
            }
            originalValue={editingOriginalValue}
            currentValue={editingCurrentValue}
            onSave={value => {
              handleSave(editing.phrase, value);
              // PLAT-10268 — the popover save persists via
              // `editTranslatedPhrase` (no span-replace confirm), so tell the
              // workspace to run the coverage check for the edited phrase, by
              // its stable source-word ids (a stale, id-less phrase yields none).
              onPhraseEditSaved?.(editing.phrase.originalElementIds ?? []);
            }}
            onCancel={handleCancel}
            canRevert={editingIsEdited}
            onRevert={() => handleRevertAndSync(editing.phrase)}
            // PLAT-11335 — the fresh verdict for the phrase under edit, shown
            // as a badge on the "Your Translation" row. Resolved by stable-id
            // overlap; a stale (id-less) phrase or an unchecked span yields
            // undefined and the popover renders exactly as before.
            coverage={resolveCoverageForPhrase?.(
              editing.phrase.originalElementIds ?? [],
            )}
            // Who last applied this change (PLAT-10444): a plain edit carries
            // it on the phrase; an override fragment carries it on its parent
            // segment record.
            editedBy={
              editing.phrase.editedBy ??
              (editing.phrase.parentId
                ? translation?.parentPhrases?.find(
                    s => s.id === editing.phrase.parentId,
                  )?.editedBy
                : undefined)
            }
            // PLAT-10852 (E15) — TM membership for the fragment being edited,
            // plus the promote action. Both resolve off data this view already
            // holds; the count is passed separately so membership still shows
            // when the entries themselves didn't resolve (DS5).
            tmTerms={resolveAppliedTerms(
              editing.phrase.tmEntryIds,
              tmEntriesById,
              tmCollectionNameById,
            )}
            tmEntryIdCount={editing.phrase.tmEntryIds?.length ?? 0}
            onPromoteToTm={
              onPromoteToTm &&
              (translationValue =>
                onPromoteToTm({
                  fragmentKey: editingFragmentKey,
                  sourceTerm: editingSourceText,
                  translation: translationValue,
                }))
            }
            // Already an entry ⇒ the popover swaps the promote action for a link
            // to it (backend provenance, or a promotion made this session).
            onViewTmEntry={
              editingTmEntry && onViewTmEntry
                ? () => onViewTmEntry(editingTmEntry)
                : undefined
            }
          />
        )}
        {/* PLAT-10268 (T8) — Atom 6: the coverage details popover, mounted at
            the Grid level (position: fixed, z 100001) anchored to the clicked
            badge. Open state lives in the workspace (`openCoverageLead`, AC 1);
            we render it only when that lead still has a fresh verdict and the
            badge's anchor rect is captured. Old-vs-current text reads from the
            covering phrases (old = the machine baseline). No split controls
            (Atom 7 / PLAT-10270). */}
        {openCoverageLead != null &&
          coverageAnchorEl &&
          (() => {
            // Resolve the cached entry by the clicked fragment's STABLE IDS, not
            // its lead: a re-fix re-tiles the segment so the fragment lead no
            // longer equals the entry key (BUG B) — mirror the badge's id
            // resolution so the popover opens on the same entry the badge shows.
            // `openCoverageLead` is that fragment's lead id; find the active
            // phrase whose lead id matches to recover its full id span.
            const openFragment = (translation?.phrases ?? []).find(
              p =>
                p.active !== false &&
                p.originalElementIds?.[0] === openCoverageLead,
            );
            const openPhraseElementIds = openFragment?.originalElementIds ?? [
              openCoverageLead,
            ];
            const entry = coverageByLead
              ? entryForPhrase(
                  coverageByLead,
                  openPhraseElementIds,
                  translation?.phrases ?? [],
                )
              : undefined;
            const verdict = entry?.verdict;
            if (!verdict || !entry) return null;
            // The open fragment's SEGMENT ID drives the split controls: its
            // `parentId` (a split/override segment), or the fragment's own lead
            // id when it is a not-yet-split machine fragment (stable-id
            // migration, DESIGN.md decision C). Split controls are inert until
            // PLAT-10270, so this is a stable placeholder key.
            const openSegmentId = openFragment?.parentId ?? openCoverageLead;
            // PLAT-10266 (Card 6) IDENTITY BRIDGE (dev merge 2026-07-27): the
            // amber/stale space is keyed by the CANONICAL numeric lead
            // (`min(originalIndices)`) — the very `staleKey` the inline rows
            // above derive — while `openCoverageLead` is a stable element id
            // after dev's stable-id migration. Derive the numeric lead from the
            // OPEN fragment so the popover's stale row and its two resolve
            // actions address exactly the fragment its badge sits on. `null`
            // when the fragment can't be resolved: the row then reads not-stale
            // and the resolve actions are withheld, never aimed at a wrong lead.
            const openStaleLead =
              openFragment && openFragment.originalIndices.length > 0
                ? Math.min(...openFragment.originalIndices)
                : null;
            // The active phrases covering the span, in source order. Current =
            // their joined `translatedValue`; old = their joined machine
            // baseline (`originalTranslatedValue ?? translatedValue`, the same
            // value Undo restores). Covering is STABLE-ID overlap; the sort is
            // source-index order (ordering only, not identity).
            const covered = new Set(entry.originalElementIds);
            const coveringPhrases = (translation?.phrases ?? [])
              .filter(
                p =>
                  p.active !== false &&
                  (p.originalElementIds?.some(id => covered.has(id)) ?? false),
              )
              .sort(
                (a, b) =>
                  Math.min(...a.originalIndices) -
                  Math.min(...b.originalIndices),
              );
            const currentTranslatedValue = coveringPhrases
              .map(p => p.translatedValue)
              .join(' ');
            // Old (previous machine) text. For an OVERRIDE span the covering
            // fragments are user-authored with no `originalTranslatedValue`, so
            // `?? translatedValue` would just echo the current text and hide the
            // row (PLAT-10268 review finding 2). The true baseline — the exact
            // value the corrected Undo (`revertOverrideSegment`) restores — is the
            // RETIRED machine fragments the override superseded. Source it from
            // them when the span is an override; otherwise fall back to the
            // per-phrase captured original (DESIGN.md ~43).
            const overrideSegmentId = translation
              ? resolveOverrideSegmentIdForElementIds(
                  translation.phrases,
                  translation.parentPhrases ?? [],
                  entry.originalElementIds,
                )
              : undefined;
            const retiredMachine = overrideSegmentId
              ? (translation?.phrases ?? [])
                  .filter(p => p.supersededBy === overrideSegmentId)
                  .sort(
                    (a, b) =>
                      Math.min(...a.originalIndices) -
                      Math.min(...b.originalIndices),
                  )
              : [];
            const oldTranslatedValue =
              retiredMachine.length > 0
                ? retiredMachine.map(p => p.translatedValue).join(' ')
                : coveringPhrases
                    .map(p => p.originalTranslatedValue ?? p.translatedValue)
                    .join(' ');
            return (
              <CoveragePopover
                anchorElement={coverageAnchorEl}
                verdict={verdict}
                oldTranslatedValue={oldTranslatedValue}
                currentTranslatedValue={currentTranslatedValue}
                onUndo={() => onCoverageUndo?.(openCoverageLead)}
                onClose={handleCoveragePopoverClose}
                // PLAT-10257 (A4) — pure pass-through: the workspace resolves
                // the covered indices + the overlap guard and opens the shared
                // two-mode span editor. Undefined when the host doesn't wire
                // it, so the popover renders no button elsewhere.
                onFixTranslation={
                  onCoverageFixTranslation
                    ? () => onCoverageFixTranslation(openCoverageLead)
                    : undefined
                }
                // PLAT-10266 (Card 4) — the popover's second home for the two
                // resolve actions (DESIGN §7). Wire the SAME workspace handlers
                // the inline menu uses, keyed by the open fragment's lead; the
                // row shows only when this fragment is stale (`staleLeads` is the
                // amber set the workspace feeds as `visibleStaleLeads`). Both
                // are undefined on hosts that don't wire them (the clip host /
                // shared prose homes) so the row never renders there (AC6).
                isStale={
                  openStaleLead !== null &&
                  (staleLeads?.has(openStaleLead) ?? false)
                }
                onResolveRetranslate={
                  onResolveRetranslate && openStaleLead !== null
                    ? () => onResolveRetranslate(openStaleLead)
                    : undefined
                }
                onResolveManual={
                  onResolveManual && openStaleLead !== null
                    ? () => onResolveManual(openStaleLead)
                    : undefined
                }
                onHoverEnter={handlePopoverHoverEnter}
                onHoverLeave={handlePopoverHoverLeave}
                segmentId={openSegmentId}
                splitControls={splitControls ?? INERT_SPLIT_CONTROLS}
              />
            );
          })()}
      </Grid>
    );
  },
);

// The translated word atom + the changed-phrase mark (PLAT-10265). Extends the
// shared `Word` (so the playhead `.current` / hover behavior is untouched —
// epic containment rule D9: no review styling leaks into the shared atom's
// home) and adds ONLY the review-scoped "edited" treatment: a green tint
// background — the brand green (`--primary` ≈ green-500) at a higher alpha than
// `--primary-tint-light` (0.08) so it reads against the dark workspace. Green is
// deliberately a different hue from the gold playhead (#dfb615) and the blue
// linked-selection highlight, so the states never read as the same thing.
//
// The mark survives the playhead: since PLAT-11765 the review marks the playing
// segment by WEIGHT (`playing`) and emits neither `.current` nor the caret, so
// nothing takes the background over and the green simply stays. `Word`'s
// `:hover { color: lightgreen !important }` still wins on text color last.
// PLAT-10270 — the per-word green mark used when only part of a "Fix
// Translation" correction changed. Same green tint as the whole-word edited
// background (`changedPhraseHighlightCss`), applied to individual changed words
// so the unchanged remainder of the segment stays unmarked.
const ChangedWordMark = styled.span`
  background-color: ${CHANGED_PHRASE_TINT};
  border-radius: 3px;
`;

/**
 * A translation the reviewer REMOVED, rendered as its struck machine baseline
 * (PLAT-11340 follow-up).
 *
 * Deliberately the source column's own redline grammar — `line-through` +
 * `indianred`, the `state-removed` rule from `TranscriptionText` — because the
 * two mean the same thing on the two sides of this screen: content that is
 * still on record but will not reach the output. A reviewer who has learned to
 * read a struck source word needs nothing new explained.
 *
 * It renders the BASELINE text, not the (empty) applied value, so the removal
 * is legible rather than a gap, and it keeps the phrase right-clickable — the
 * struck ghost is the only handle "Restore Translation" has.
 */
const RemovedTranslationGhost = styled.span`
  text-decoration: line-through;
  color: indianred;
`;

/**
 * PLAT-11906 — a translated fragment the dub's read frontier may sit inside:
 * with `readChars`, the text after that many characters is drawn unread.
 */
const TranslatedFragment: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & { readChars?: number }
> = ({ readChars, children, ...rest }) => (
  <TranslatedWord {...rest}>
    {readChars === undefined
      ? children
      : wrapUnreadTail(children, readChars, UnreadTail)}
  </TranslatedWord>
);

/** The part of a fragment the dub has not reached: the unread colour the
 * whole-fragment `unread` class uses. */
const UnreadTail = styled.span`
  color: var(--text-muted);
`;

// PLAT-12024 — the just-changed wash, the same keyframe `TranscriptionTextTranslated`
// paints on its word atoms (design system, "Just Changed (Landed) Pulse").
const redubLandedFade = keyframes`
  0% {
    background-color: color-mix(in srgb, var(--primary) 28%, transparent);
  }
  70% {
    background-color: color-mix(in srgb, var(--primary) 28%, transparent);
  }
  100% {
    background-color: transparent;
  }
`;

const TranslatedWord = styled(Word)`
  ${changedPhraseHighlightCss}
  ${staleFragmentHighlightCss}

  /* PLAT-12024 — a re-dub just landed on this fragment. */
  &.redub-landed {
    border-radius: 3px;
    animation: ${redubLandedFade} 6000ms ease-out forwards;
  }

  /* PLAT-11650 — while the in-place field is open the span is a plain host:
     no fill, no hover tint (Word's own hover is !important, hence the
     specificity here). The playhead variants this used to neutralize went with
     the caret in PLAT-11765; the review emits no .current at all now. */
  &.inline-editing {
    color: var(--text-primary);
    background-color: transparent;
    cursor: text;
  }
  &.inline-editing:hover {
    color: var(--text-primary) !important;
  }
`;

// PLAT-10268 (T8) QA polish — coverage indicators occupy their own line at the
// top of the translated Row, immediately below its timestamp. This keeps the
// translated text in a separate flow so it cannot overlap a badge or spinner.
// The row stays inside the aligned content Row, so cross-column height matching
// includes it. `contentEditable={false}` + non-selectable keeps controls out of
// text selection.
// The score's line, in the BOTTOM-RIGHT CORNER OF THE GREY AREA (Denys,
// 2026-07-14): it is the LAST child of the block's `Row` (which paints the
// tint), so it sits inside the grey and below the words.
// `display: flex; width: 100%` is what guarantees "always a new line" — a
// full-width block cannot share a line with the inline words, whatever the text
// does. `justify-content: flex-end` pins it to the right corner; `margin-top` is
// the gap from the last line of text (design-system token, never a hardcoded px).
const CoverageRow = styled.div.attrs({ contentEditable: false })`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  margin-top: var(--spacing-xs);
  user-select: none;
`;

// Reserves the pill height so a spinner→badge swap does not jump the line.
const CoverageSlot = styled.span`
  display: inline-flex;
  align-items: center;
`;

// The in-flight spinner, sized to sit inline with the badge pill.
const CoverageSpinner = styled(Spinner)`
  width: 14px;
  height: 14px;
`;

// PLAT-11678 — the automatic re-dub's one-line status under a block's
// translated text. Same footing as `CoverageRow` (full width, breaks the
// inline word flow, inside `Row` so the height-match pass sees it), read as
// helper text: sentence case, secondary colour; the failed state takes the
// error colour so it is found without hunting.
const DubStatusRow = styled.div.attrs({ contentEditable: false })<{
  $failed: boolean;
}>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  margin-top: var(--spacing-xs);
  font-size: var(--text-xs);
  color: ${p => (p.$failed ? 'var(--destructive)' : 'var(--text-secondary)')};
  user-select: none;
`;

const DubStatusDot = styled.span`
  color: var(--text-muted);
`;

// PLAT-11482 — one word inside a translated phrase the splitter could not
// fragment. Purely a rendering subdivision: the phrase remains the click /
// context-menu / edit unit, and these spans carry no `data-initial-index`, so
// selection, the active-phrase scroll and the span-replace path all still see
// exactly one word per phrase. `display: inline` keeps text selection flowing
// across them (CLAUDE.md transcript pitfall 11).
const TranslatedWordAtomSpan = styled.span`
  display: inline;
  border-radius: 2px;
`;

// PLAT-10850 (E13) — the gold underline on ONLY the matched glossary term inside
// a TM-backed translated fragment (not the whole fragment). A nested span inside
// the `TranslatedWord`, so it doesn't disturb the Word's `data-initial-index` /
// click-to-seek / selection. NOTE (design system): gold `--accent` is used here
// as an informational term marker; confirm the token at review.
const TmTermMark = styled.span`
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--accent) 70%, transparent);
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
  text-decoration-skip-ink: none;
`;

/**
 * Render a translated fragment's text with ONLY its matched glossary term(s)
 * wrapped in {@link TmTermMark} (PLAT-10850). `terms` are the resolved entry
 * strings (target term, or source term for do-not-translate). Falls back to the
 * plain string when there's nothing to mark, so a non-TM fragment is untouched.
 */
function renderTmMarkedText(
  text: string,
  terms: readonly string[],
): React.ReactNode {
  if (terms.length === 0) return text;
  const ranges = findTmTermRanges(text, terms);
  if (ranges.length === 0) return text;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], i) => {
    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <TmTermMark key={`tm-${i}`}>{text.slice(start, end)}</TmTermMark>,
    );
    cursor = end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/**
 * PLAT-12034 — a fragment's text with its delivery-marker chips at their
 * offsets. The text between chips is still glossary-marked. A chip is its own
 * element with no children, so the unread-tail slicing leaves it alone.
 */
function renderTextWithMarkers(
  text: string,
  terms: readonly string[],
  placed: readonly PlacedMarker[] | undefined,
  lines: ReadonlyMap<string, LineMarkers> | undefined,
  actions: {
    busy: boolean;
    onRemove?: (lineId: string, taggedText: string | null) => void;
    onEdit?: (lineId: string, anchor: HTMLElement) => void;
    /** Lines edited since they were voiced: no Remove on their chips. */
    editedLines?: ReadonlySet<string>;
  },
): React.ReactNode {
  if (!placed || placed.length === 0 || !lines) {
    return renderTmMarkedText(text, terms);
  }
  const sorted = [...placed].sort((a, b) => a.offset - b.offset);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((marker, i) => {
    const at = Math.min(Math.max(marker.offset, cursor), text.length);
    if (at > cursor) {
      nodes.push(
        <React.Fragment key={`t-${i}`}>
          {renderTmMarkedText(text.slice(cursor, at), terms)}
        </React.Fragment>,
      );
      cursor = at;
    }
    const line = lines.get(marker.lineId);
    nodes.push(
      <DeliveryMarkerChip
        key={`m-${marker.lineId}-${marker.index}`}
        tag={marker.tag}
        busy={actions.busy}
        onRemove={
          actions.onRemove && line && !actions.editedLines?.has(line.lineId)
            ? () => {
                const remaining = line.markers.filter(
                  (_, index) => index !== marker.index,
                );
                actions.onRemove?.(
                  marker.lineId,
                  remaining.length > 0
                    ? serializeLineMarkers(line.words, remaining)
                    : null,
                );
              }
            : undefined
        }
        onEdit={
          actions.onEdit
            ? anchor => actions.onEdit?.(marker.lineId, anchor)
            : undefined
        }
      />,
    );
  });
  if (cursor < text.length) {
    nodes.push(
      <React.Fragment key="t-end">
        {renderTmMarkedText(text.slice(cursor), terms)}
      </React.Fragment>,
    );
  }
  return nodes;
}

// Two equal columns filling the pane area; each column scrolls independently
// (and is scroll-mirrored by `useScrollSync`). A middle divider matches the
// workspace's `Pane` border. `$single` is the expanded-video layout: the
// translated column alone, taking the whole pane.
const Grid = styled.div<{ $single: boolean }>`
  display: grid;
  grid-template-columns: ${props => (props.$single ? '1fr' : '1fr 1fr')};
  height: 100%;
  min-height: 0;
`;

const Column = styled.div`
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px;
  // Transcript reading size — matches the prose transcript's Main container
  // (Transcript.tsx), so the review reads at the same size as every other
  // transcript surface. Without this the word atoms (which set no font-size)
  // inherit the 16px body default.
  font-size: var(--text-sm);
`;

// One aligned row. The active row gets a subtle elevated background in BOTH
// columns (the lockstep highlight); the word atoms additionally carry the
// `playing` weight (on WordLight and on the translated Word) and the `read`
// class marking how far playback has got. `$changeHover` (the
// hovered Changes card fully covers this block) wins with the blue
// correspondence wash — one calm row tint instead of painting every word.
// PLAT-11507 — how far the row text sits inside its tint. Shared with the
// speaker header and timestamp rows so the three stay on one vertical line.
const ROW_TEXT_INSET = 'var(--spacing-sm)';

const Row = styled.div<{
  $active: boolean;
  $changeHover?: boolean;
  $deletion?: boolean;
}>`
  // PLAT-11507 — the row tint is a block INSET from the pane on both sides
  // (the Column's own padding does that), with the text padded inside it so a
  // short word never sits flush against the tint's edge.
  //
  // That padding moves the text inward, so the flat sibling rows above it —
  // the speaker header and the timestamp — carry the SAME inset
  // (the ROW_TEXT_INSET constant). All three keep lining up; change it and
  // they move together.
  padding: var(--spacing-xs) ${ROW_TEXT_INSET};
  border-radius: var(--radius-button, 4px);
  margin-bottom: var(--spacing-xs);
  line-height: 1.5;
  background: ${props =>
    props.$changeHover
      ? LINKED_ROW_TINT
      : props.$active
        ? 'var(--surface-elevated)'
        : 'transparent'};

  &:last-child {
    margin-bottom: 0;
  }

  /* PLAT-11340 follow-up — the deletion score: a change bar in the row's own
     margin for a block where words were REMOVED and nothing added. It replaces
     the green tint for that case, because the tint says "these words are new"
     and after a deletion none of the words still on screen are — so tinting the
     sentence marked it as rewritten when it had only been shortened.

     Keeps the --primary hue the "edited" mark already uses, so the vocabulary
     is unchanged: same meaning, stated as a rule instead of a wash.

     Drawn as a pseudo-element rather than a left border deliberately. The row's
     horizontal padding (ROW_TEXT_INSET) is what the FLAT SIBLING rows above it
     — the speaker header and the timestamp — align their own inset to; a real
     border would push this row's text inward and break that alignment. */
  position: ${p => (p.$deletion ? 'relative' : 'static')};

  ${p =>
    p.$deletion
      ? `&::before {
    content: '';
    position: absolute;
    left: 0;
    top: var(--spacing-xs);
    bottom: var(--spacing-xs);
    width: 2px;
    border-radius: 1px;
    background: var(--primary);
  }`
      : ''}
`;

const Empty = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: 20px;
  text-align: center;
`;

// Speaker header / timestamp rows (PLAT-10264 — Option-A reversal). The look
// mirrors the prose renderer's equivalents (`SpeakerHeader` / `SpeakerLabel` /
// `SpeakerTime` / `TimestampRow` in TranscriptionText.tsx) — same colors, font
// sizes, and the label-left / time-right layout — defined LOCALLY because those
// prose styled components are module-private there (not exported). Differences
// from the prose versions, fitted to the aligned rows:
//   - `SpeakerLabel` drops the prose `$clickable` branch — the review aligned
//     view has no `onSpeakerClick`, so the label is purely a non-interactive
//     section heading.
//   - `TimestampRow` drops the prose `margin-top: -11px` (which pulls the time
//     up into the prose's 12px paragraph gap). The aligned rows have a smaller
//     4px gap and no leading-whitespace indent, so a positive small gap reads
//     correctly instead of overlapping the previous row.
// All header rows are `contentEditable={false}` + non-selectable.
const SpeakerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  // PLAT-11507 — match the row text's inset so the label and the words below
  // it share one left edge.
  padding-left: ${ROW_TEXT_INSET};
  padding-right: ${ROW_TEXT_INSET};
  margin-bottom: 2px;
  margin-top: 10px;
  user-select: none;
  pointer-events: none;

  &:first-child {
    margin-top: 0;
  }
`;

const SpeakerLabel = styled.span<{ $color?: string }>`
  color: ${props => props.$color || 'var(--accent)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// Timestamps are the least important header info in the review (the reviewer
// reads text, not the clock) — one step below --text-xs, review-scoped only
// (the prose panes keep their sizes).
const SpeakerTime = styled.span`
  color: var(--text-muted);
  font-size: 10px;
  font-weight: var(--font-weight-regular);
`;

const TimestampRow = styled.div`
  text-align: right;
  // PLAT-11507 — right-aligned, so the inset keeps it flush with the row
  // text's right edge rather than the pane's.
  padding-right: ${ROW_TEXT_INSET};
  color: var(--text-muted);
  font-size: 10px;
  font-weight: var(--font-weight-regular);
  line-height: 1;
  margin-top: 4px;
  margin-bottom: 2px;
  user-select: none;
  pointer-events: none;
`;
